

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { buildTop, buildGrommets, grommetXs } from './ctb-geometry-tops.js?v=20261015b';
import { buildAccessories } from './ctb-geometry-accessories.js?v=20261015b';
import { buildBaseSet, baseCenters, baseReach, orderedWidths, wireOpening, fitsOpening, NOTCH_NUDGE } from './ctb-geometry-bases.js?v=20261015b';
import { loadManifest, partFor, glbPart, loadLegs, credenzaPart } from './ctb-parts.js?v=20261015b';
import { METALS, setChromeEnv } from './ctb-materials.js?v=20261015b';
import { groupParts } from './ctb-partgroups.js?v=20261015b';
import { assetUrl, dataUrl } from './ctb-asseturl.js?v=20261015b';
import { edgeIcon, EDGE_PROFILES } from './ctb-geometry-edges.js?v=20261015b';
import { applyRetail, buildBOM, compatibleBases, compatibleFamilies, CREDENZA_EDGE, money, revealLine, rowPrice, topPrice } from './ctb-pricing.js?v=20261023a';
import * as telemetry from './ctb-telemetry.js?v=20261011a';

const FILL_DEFAULT = 2.0;   // side fill strength (see the lights below): upright faces about as bright as the top
const TABLE_H = 29;
// Where the credenza stands (Jeremy 2026-10-15: "on the side of the table or the top/bottom, so they can visualize it"):
// behind or in front of a long side, or past either end; always CREDENZA_CLEAR away, doors toward the table.
// A table that is the same front to back and end to end needs only two (Jeremy 2026-10-15): Side (along a long side)
// and Front/Back (past an end, the head or foot of the table). A sightline top is wide at one end and narrow at the screen end (+x), so it keeps four.
const credPlaces = () => (st.shape === 'sightline'
  ? { back: 'Behind the table', front: 'In front of the table', left: 'At the wide end', right: 'At the narrow (screen) end' }
  : { back: 'Side', right: 'Front/Back' });
const credPlaceText = place => (st.shape === 'sightline'
  ? { back: 'behind the table', front: 'in front of the table', left: 'past the wide end of the table', right: 'past the narrow (screen) end of the table' }
  : { back: 'along a long side of the table', right: 'past one end of the table' })[place] || 'behind the table';
// the camera on the Credenza step: across the table from the credenza, a little to one side, so its doors face you
const CRED_VIEW = { back: [-0.55, 0.6, 1], front: [0.55, 0.6, -1], left: [1, 0.6, 0.45], right: [-1, 0.6, -0.45] };   // credenza upper left, clear of the price box
const credView = () => (st.credenza?.type ? new THREE.Vector3(...CRED_VIEW[st.credenza.place || 'back']) : null);
const CREDENZA_CLEAR = 48;   // inches between the table edge and a credenza behind it: room to pull a chair out and open the doors

const QS = new URLSearchParams(location.search);

const DEV = !QS.has('simple');
const price = v => (DEV ? v : '');
const PDP = (QS.get('pdp') || '').trim().toUpperCase() || null;
let LOCK = null;
const $ = s => document.querySelector(s);

const REL = new URL(import.meta.url).searchParams.get('v'), relQ = REL ? `?v=${REL}` : '';
const noDash = s => String(s).replace(/\s*—\s*/g, ': ');
const el = (tag, cls, html) => { const e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; };

const OWN_PICTURES = true;
const PHOTO_FINISH = {
  'T-0943': 'Straight Noce', 'T-10624': 'Fawn Cypress', 'T-1529': 'Portico Teak', 'T-1530': 'Portico Teak',
  'T-1531': 'Portico Teak', 'T-1819': 'Portico Teak', 'T-1867': 'Fawn Cypress', 'T-1868': 'California Walnut',
  'T-2991': 'Grigio Notte', 'T-2994': 'Portico Teak', 'T-2997': 'Fawn Cypress', 'T-2999': 'Fawn Cypress',
  'T-3108': 'Straight Noce', 'T-3109': 'Iron Hemlock II', 'T-3113': 'California Walnut', 'T-3115': 'Portico Teak',
  'T-3118': 'Portico Teak',
};

const PRESET_PRICE = {
  'T-0943': 3599, 'T-10624': 4099, 'T-1529': 1599, 'T-1530': 1499, 'T-1531': 1299, 'T-1819': 4599, 'T-1867': 7299,
  'T-1868': 2999, 'T-2991': 13899, 'T-2994': 7999, 'T-2997': 1599, 'T-2999': 1799, 'T-3108': 5399, 'T-3109': 3899,
  'T-3113': 5899, 'T-3115': 4799, 'T-3118': 1399,
};

const PAGE_WITHOUT_POWER = new Set(['T-10624', 'T-3109', 'T-3113', 'T-3115']);

const PRESET_TOTAL = { 'T-10624': 5599, 'T-3109': 5899, 'T-3113': 7899, 'T-3115': 6799 };

const x99 = v => Math.round((v + 1) / 100) * 100 - 1;

const PRESET_FIX = { 'T-10624': { edge: 'A1', laminate: 'tfl', revealColor: 'WT' } };

window.CTB_STARTED = true;
{ const late = $('#noload'); if (late && !late.hidden) { late.hidden = true; document.body.classList.remove('no-gl'); } }

const HAS_GL = (() => { try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (e) { return false; } })();
if (!HAS_GL) {
  document.body.classList.add('no-gl'); const p = $('#nogl'); if (p) p.hidden = false;
  if (window.CTB_REPORT) CTB_REPORT('nogl');
  throw new Error('Build your own: WebGL is not available, showing the fallback panel');
}
const canvas = $('#c');

let renderer;
try { renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true }); }
catch (e) {
  document.body.classList.add('no-gl'); const p = $('#nogl'); if (p) p.hidden = false;
  if (window.CTB_REPORT) CTB_REPORT('renderer');
  throw new Error('Build your own: the 3-D renderer could not be created, showing the fallback panel: ' + (e && e.message));
}
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.localClippingEnabled = true;
renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 0.58;
renderer.outputColorSpace = THREE.SRGBColorSpace;
const scene = new THREE.Scene();
scene.background = new THREE.Color('#f3f4f6');
scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;
setChromeEnv(renderer, ...(QS.get('chrome') || '').split(',').filter(Boolean).map(Number));
const camera = new THREE.PerspectiveCamera(32, 1, 1, 5000);
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true; controls.minDistance = 40; controls.maxDistance = 900;
controls.maxPolarAngle = Math.PI / 2 + 0.45;
controls.addEventListener('start', telemetry.orbitStart); controls.addEventListener('end', telemetry.orbitEnd);

const hemi = new THREE.HemisphereLight('#ffffff', '#d9dde3', 0.55); scene.add(hemi);

const bounce = new THREE.DirectionalLight('#ffffff', QS.has('bounce') ? +QS.get('bounce') : 1.2);
bounce.position.set(0, -100, 0); scene.add(bounce);
// Side fill (Mr. Sir / Jeremy 2026-10-15: "darker than it should be, can barely see the CW leg color"): the sun is high, so
// upright faces (base panels, legs, top edges, credenzas) lit at ~55% of the top and a dark laminate read near-black.
// Four level lights from the diagonals, no shadows: a level light adds nothing to an upward face, so the calibrated top
// is untouched, while every upright face gets lifted. ?fill=<intensity> to tune.
const FILL = QS.has('fill') ? +QS.get('fill') : FILL_DEFAULT;
for (const [x, z] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) { const f = new THREE.DirectionalLight('#ffffff', FILL); f.position.set(x * 100, 0, z * 100); scene.add(f); }
const sun = new THREE.DirectionalLight('#ffffff', 1.6);
sun.position.set(120, 260, 160); sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048); sun.shadow.bias = -0.0004; sun.shadow.normalBias = 0.6;
Object.assign(sun.shadow.camera, { left: -260, right: 260, top: 260, bottom: -260, near: 10, far: 800 });
scene.add(sun);
const floor = new THREE.Mesh(new THREE.CircleGeometry(700, 64), new THREE.ShadowMaterial({ opacity: 0.22 }));
floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; scene.add(floor);

function floorTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 512;
  const g = c.getContext('2d'), grd = g.createRadialGradient(256, 256, 0, 256, 256, 256);
  grd.addColorStop(0, '#e4e6ea'); grd.addColorStop(0.45, '#ecedf0'); grd.addColorStop(1, '#f3f4f6');
  g.fillStyle = grd; g.fillRect(0, 0, 512, 512);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}
const floorTint = new THREE.Mesh(new THREE.PlaneGeometry(2400, 2400), new THREE.MeshBasicMaterial({ map: floorTexture(), toneMapped: false }));
floorTint.rotation.x = -Math.PI / 2; floorTint.position.y = -0.05; scene.add(floorTint);

if (QS.get('bg') === 'white') { scene.background = new THREE.Color('#ffffff'); floorTint.visible = false; }

let table = new THREE.Group(); scene.add(table);

function resize() {
  const w = canvas.clientWidth, h = canvas.clientHeight;
  if (canvas.width !== w * renderer.getPixelRatio() || canvas.height !== h * renderer.getPixelRatio()) {
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
  }
}

function fitDistance(bb, dir, margin = 1.14, aspect = null) {
  if (aspect == null) resize(); else { camera.aspect = aspect; camera.updateProjectionMatrix(); }
  const up = new THREE.Vector3(0, 1, 0);
  const right = new THREE.Vector3().crossVectors(up, dir);
  if (right.lengthSq() < 1e-6) right.set(1, 0, 0); else right.normalize();
  const camUp = new THREE.Vector3().crossVectors(dir, right).normalize();
  const tanV = Math.tan((camera.fov * Math.PI) / 360), tanH = tanV * camera.aspect;
  let need = 0;
  const p = new THREE.Vector3();
  for (let i = 0; i < 8; i++) {
    p.set(i & 1 ? bb.max.x : bb.min.x, i & 2 ? bb.max.y : bb.min.y, i & 4 ? bb.max.z : bb.min.z).sub(controls.target);
    const cz = p.dot(dir);
    need = Math.max(need, Math.abs(p.dot(right)) / tanH + cz, Math.abs(p.dot(camUp)) / tanV + cz);
  }
  return Math.max(need * margin, controls.minDistance);
}
function frameTable(keepAngle = true, fromDir = null) {
  const bb = new THREE.Box3().setFromObject(table);
  if (bb.isEmpty()) return;
  const ctr = bb.getCenter(new THREE.Vector3());
  controls.target.set(ctr.x, ctr.y * 0.8, ctr.z);
  const dir = fromDir ? fromDir.clone().normalize() : keepAngle && camera.position.length() > 0
    ? camera.position.clone().sub(controls.target).normalize()
    : new THREE.Vector3(0.72, 0.55, 0.78).normalize();
  camera.position.copy(controls.target).add(dir.multiplyScalar(fitDistance(bb, dir)));
  controls.update();
}
function tick() { resize(); controls.update(); renderer.render(scene, camera); requestAnimationFrame(tick); }
tick();
$('#spin').addEventListener('change', e => { controls.autoRotate = e.target.checked; telemetry.track('auto_rotate', { on: e.target.checked }); });
$('#reset').addEventListener('click', () => { frameTable(false); telemetry.track('reset_view'); });
let resizeT; addEventListener('resize', () => { clearTimeout(resizeT); resizeT = setTimeout(() => frameTable(true), 150); });

let CAT = null;
const st = {
  shape: 'rectangular', sizeKey: '48x96', pieces: 1, multi: false, laminate: 'tfl', edge: 'A1',
  finish: null, midFinish: null, rounded: false, reveal: false,
  famCode: 'BPN', baseSku: null, baseFinish: null, sideFinish: null, metalCode: 'SV', kick: 'BK',
  baseOpts: {}, grommet: { sku: '', color: 'BK', qty: 0, rot: 0 }, acc: {}, typical: null, height: 'seated', doors: 0, revealColor: 'BK',
  credenza: { type: '', depth: 24, width: 72, qty: 1, locks: false, place: 'back' },   // a matching credenza near the table (book p.168-170); place: back | front | left | right
  baseLinked: true,
  whiteGlove: false,
};
let TYP = null;
const ST_DEFAULT = JSON.stringify(st);
const fin = name => CAT.finishes.find(f => f.name === name) || CAT.finishes[0];
const woodEdge = code => CAT.edges[code]?.edge === 'wood';

const baseKind = (fam, i, n) => fam.geo === 'cylmix' ? (i === 0 || i === n - 1 ? 'halfcyl' : 'cylinder') : (fam.wirebox && i > 0 && i < n - 1) ? 'wirebase' : fam.geo;
function baseFootprints(top, row, fam) {
  const centers = !row || fam.geo === 'cornerlegs' ? [] : baseCenters(row, fam, top, CAT.placement);
  const n = centers.length; if (!n) return [];
  const widths = orderedWidths(row, top, n);
  const kind = i => baseKind(fam, i, n);

  const reach = (i, side) => fam.metal && kind(i) !== 'wirebase' ? (fam.double && i > 0 && i < n - 1 ? 5.5 : 1.5) : baseReach(kind(i), { w: widths[i], d: fam.depth ?? widths[i], top, side, i, n });
  return centers.map((c, i) => [c - reach(i, -1), c + reach(i, 1)]);
}

function clearSpans(top, row, fam) {
  const fp = baseFootprints(top, row, fam);
  if (!fp.length) return [[-top.length / 2 + 2, top.length / 2 - 2]];
  const spans = [[-top.length / 2 + 2, fp[0][0]]];
  for (let i = 0; i < fp.length - 1; i++) spans.push([fp[i][1], fp[i + 1][0]]);
  spans.push([fp[fp.length - 1][1], top.length / 2 - 2]);
  return spans;
}

const PEDESTAL = { RDDK: 'power', PRPL: 'power', SQDK: 'none', BMMX: 'none', ARTB: 'none', BLMX: 'none' };
const COLUMN_CLEAR = 6;
const cutHalf = () => (st.grommet.rot ? 4.8 : 8.4) / 2;

const GROMMET_CUT = { 'AC-DPGR-92': [8.4, 4.8], 'AC-DPGR-94': [9.13, 4.75], 'AC-DPGR-74': [14.12, 9], 'OS-PG3': [10.23, 5.78],
  'AC-DPGR-78': [9.3, 4.8] };

const CUT_UNCONFIRMED = new Set(['AC-DPGR-78']);
const isPowerSku = sku => /-EL\d/.test(sku || '');
const powerSkuOf = sku => sku.replace(/^(CF-RDDK|ML-PRPL)-(\d)/, '$1-EL$2');
const regularSkuOf = sku => sku.replace(/-EL(\d)/, '-$1');

function overColumns(top, row, fam, xs) {
  if (!row || !xs.length || /^ML-PRPL-04/.test(row.sku)) return 0;
  const centers = baseCenters(row, fam, top, CAT.placement), h = cutHalf();
  return xs.filter(x => centers.some(c => Math.abs(x - c) < COLUMN_CLEAR + h)).length;
}

function notchOverlaps(top, row, fam, trustEstimates) {
  const notch = CAT.options.find(o => o.sku === 'OP-CONF-NOTCH');
  if (!row || !st.grommet.sku || !st.grommet.qty || fam.placement === 'single' || !notch.families.includes(fam.family)) return 0;

  const cut = GROMMET_CUT[st.grommet.sku], [len, wid] = cut || [8.4, 4.8];
  const along = st.grommet.rot ? wid : len, across = st.grommet.rot ? len : wid, h = along / 2;
  const xs = grommetXs(top, st.grommet.qty), centers = baseCenters(row, fam, top, CAT.placement), n = centers.length;
  return baseFootprints(top, row, fam).filter(([a, b], i) => xs.some(x => x + h > a + NOTCH_NUDGE && x - h < b - NOTCH_NUDGE
    && !(cut && (trustEstimates || !CUT_UNCONFIRMED.has(st.grommet.sku)) && fitsOpening(wireOpening(fam, baseKind(fam, i, n)), x - centers[i], along, across)))).length;
}

const notchCount = (top, row, fam) => top?.customLength ? 0 : notchOverlaps(top, row, fam, true);
const notchRisk = (top, row, fam) => notchOverlaps(top, row, fam, false) - notchOverlaps(top, row, fam, true);
function spineSpots(top, row, fam) {
  if (!row) return [0];
  const spans = clearSpans(top, row, fam);
  if (spans.length === 1) return [0];
  return spans.slice(1, -1).filter(([a, b]) => b - a >= 14).map(([a, b]) => (a + b) / 2).sort((a, b) => Math.abs(a) - Math.abs(b));
}
const PLATE_GROMMETS = ['AC-DPGR-92', 'AC-DPGR-74'];

const PLATE_TEXT = {
  'AC-DPGR-ABB': ['Network and phone jacks', 'Two network jacks and a phone jack, built into the grommet.'],

  'AC-DPGR-C45LC51': ['HDMI, VGA and audio connections', 'Plug a laptop into the room\'s TV or projector.'],
};

const GROMMET_INFO = {

  'OS-PG3': { title: '3 outlets, 2 USB-A, 2 USB-C', shown: 'black', summary: '3 outlets, 2 USB-A and 2 USB-C charging ports, and a brush-lined cable slot. 12 ft cord.' },
  'AC-DPGR-92': { title: '2 outlets, USB, plate slot', shown: 'silver', summary: '2 outlets on top and 1 underneath, USB-A and USB-C charging, and a slot for a connection plate.' },

  'AC-DPGR-94': { title: '4 outlets, USB, network', shown: 'black', summary: '4 outlets on top and 1 underneath, USB-A and USB-C charging, and 2 openings for network jacks.' },
  'AC-DPGR-78': { title: '3 outlets, USB, HDMI', shown: 'silver', summary: '3 outlets, 2 USB and 2 USB-C charging ports, an HDMI port and a network jack.' },
  'AC-DPGR-74': { title: '6 outlets, USB, plate slot', shown: 'silver', summary: '6 outlets, USB-A and USB-C charging, and a slot for a connection plate. 15" long.' },
};
const GROMMET_IMG = {
  'OS-PG3': 'assets/grommets/OS-PG3.jpg',

  'AC-DPGR-78': 'assets/grommets/AC-DPGR-78.png',

  'AC-DPGR-92': 'assets/grommets/AC-DPGR-92.jpg',

  'AC-DPGR-94': 'assets/grommets/AC-DPGR-94.jpg',

  'AC-DPGR-74': 'assets/grommets/AC-DPGR-74.jpg',
};

const GROMMET_IMG_LARGE = { 'AC-DPGR-78': 'assets/grommets/pricebook-AC-DPGR-78.png' };
const grommetImg = sku => assetUrl(`${GROMMET_IMG[sku] || `assets/grommets/pricebook-${sku}.png`}${relQ}`);
const grommetImgLarge = sku => (GROMMET_IMG_LARGE[sku] ? assetUrl(GROMMET_IMG_LARGE[sku] + relQ) : grommetImg(sku));

const GROMMET_PRICE = { 'AC-DPGR-92': 699, 'AC-DPGR-94': 799, 'AC-DPGR-78': 1199, 'AC-DPGR-74': 1899 };

const OUR_GROMMET = {
  sku: 'OS-PG3', scope: 'grommet', label: 'Power/USB grommet: 3 outlets, 2 USB-A, 2 USB-C', colors: { BK: 399, SV: 399 },

  spec: { size: 'About 10-1/4" x 5-3/4"', features: ['(3) power outlets', '(2) USB-A + (2) USB-C charging ports', 'On/off switch', 'Cords pass through the brush with the lid closed', '144" (12 ft) plug cord'], finishes: 'Black, Silver' },
};

function priceGrommets(cat) {
  for (const o of cat.options.filter(o => o.scope === 'grommet')) {
    const set = GROMMET_PRICE[o.sku]; if (!set) continue;
    const low = Math.min(...Object.values(o.colors));
    for (const k of Object.keys(o.colors)) o.colors[k] = o.colors[k] === low ? set : x99(o.colors[k]);
  }
  const { spec, ...opt } = OUR_GROMMET;
  cat.options.unshift(opt); cat.grommetSpecs[opt.sku] = spec;
}

const COUNT = ['', 'one', 'two', 'three', 'four', 'five', 'six'];
const specLine = s => noDash(s).replace(/\((\d)\)\s*/g, (m, n) => (COUNT[n] || n) + ' ').replace(/\s\+\s/g, ' and ').replace(/interface plate/gi, 'connection plate')
  .replace(/RJ-45 keystone knock-outs/gi, 'network-jack openings').replace(/RJ-45 keystone jack/gi, 'network jack').replace(/keystone openings/gi, 'network-jack openings')
  .replace(/\bCANNOT\b/g, 'Cannot').replace(/^./, c => c.toUpperCase());
const basketLen = sku => parseInt(sku.slice(-2), 10);

function centreRun(row, fam) {
  if (row && !fam.metal && row.bases > 1) return { name: 'wire trough', off: 13.5 / 2 + 5 };
  if (row && fam.metal && row.bases > 1 && !['ymetal', 'cornerlegs'].includes(fam.geo)) return { name: 'spanning rod', off: 5.5 };
  return null;
}

function basketRoom(top, row, fam, L) {
  const GAP = 2, run = centreRun(row, fam);
  const spans = clearSpans(top, row, fam).map(([a, b]) => [a + 0.25, b - 0.25]).filter(([a, b]) => b - a >= L);
  const grid = [];
  for (const [a, b] of spans) {
    const n = Math.floor((b - a + GAP) / (L + GAP)), x0 = (a + b) / 2 - (n * L + (n - 1) * GAP) / 2 + L / 2;
    for (let k = 0; k < n; k++) grid.push(x0 + k * (L + GAP));
  }
  return { GAP, spans, grid, zs: run ? [run.off, -run.off] : [0] };
}

function basketCap(top, row, fam, L) { const r = basketRoom(top, row, fam, L); return r.grid.length * r.zs.length; }

function basketSlots(top, row, fam, L, gxs = [], qty = 0) {
  const { GAP, spans, grid, zs } = basketRoom(top, row, fam, L), targets = gxs.length ? gxs : [0];
  const want = Math.min(qty, grid.length * zs.length);
  const placed = zs.map(() => []), out = [];
  for (let i = 0; i < want; i++) {
    const t = targets[i % targets.length], r = Math.floor(i / targets.length) % zs.length;
    let best = null;
    for (const [a, b] of spans) {
      let free = [[a, b]];
      for (const [p, q] of placed[r]) free = free.flatMap(([u, v]) => [[u, Math.min(v, p - GAP)], [Math.max(u, q + GAP), v]]).filter(([u, v]) => v - u >= L);
      for (const [u, v] of free) { const x = Math.max(u + L / 2, Math.min(v - L / 2, t)); if (!best || Math.abs(x - t) < best.d) best = { x, d: Math.abs(x - t) }; }
    }
    if (!best) break;
    placed[r].push([best.x - L / 2, best.x + L / 2]); out.push({ x: best.x, z: zs[r] });
  }
  if (out.length === want) return out;
  const near = x => Math.min(...targets.map(t => Math.abs(x - t)));
  return grid.flatMap(x => zs.map(z => ({ x, z }))).sort((p, q) => near(p.x) - near(q.x) || q.z - p.z).slice(0, want);
}
const HPL_ONLY_BASES = ['imperial', 'komo'];

const CUSTOM_LENGTHS = { 180: 192 };
function addCustomLengths(cat) {
  for (const [len, from] of Object.entries(CUSTOM_LENGTHS).map(([a, b]) => [+a, +b])) {
    if (cat.tops.some(t => t.customLength === from && t.length === len)) continue;

    const real = t => cat.tops.some(r => r.length === len && !r.customLength && r.shape === t.shape && r.depth === t.depth && r.pieces === t.pieces && r.multiFinish === t.multiFinish);
    const tops = [];
    for (const t of cat.tops) { if (t.length === from && !real(t)) tops.push({ ...t, length: len, customLength: from }); tops.push(t); }
    cat.tops = tops;
    for (const b of cat.bases) if (!b.single) for (const s of b.sizes.filter(s => s.length === from)) if (!b.sizes.some(x => x.length === len && x.depth === s.depth)) b.sizes.push({ ...s, length: len });
  }
}

const customNote = top => top?.customLength ? `${ft(top.length)} ft length` : '';

const customForSales = top => top?.customLength ? `${ft(top.length)} ft (${top.length}") top: a factory CUSTOM, designer quote, standard made-to-order lead time. Codes below are the ${ft(top.customLength)} ft parts; designers adjust the base (any notch too, none is priced). Price to the customer stays as shown.` : '';
const shippable = t => t.shape === 'round' || t.shape === 'square' || t.length / t.pieces <= 96;
function topsForShape() { return CAT.tops.filter(t => t.shape === st.shape && shippable(t) && (!LOCK || compatibleBases(CAT, t, LOCK.famCode).length > 0)); }
const familiesFor = top => (LOCK ? [LOCK.famCode] : compatibleFamilies(CAT, top));
function sizeKeyOf(t) { return `${t.depth}x${t.length}`; }
function currentTop() {
  const cands = topsForShape().filter(t => sizeKeyOf(t) === st.sizeKey);
  return cands.find(t => t.pieces === st.pieces && t.multiFinish === st.multi) || cands.find(t => t.multiFinish === st.multi) || cands[0] || topsForShape()[0];
}
function validate() {

  const tops = topsForShape();
  if (!tops.some(t => sizeKeyOf(t) === st.sizeKey)) st.sizeKey = sizeKeyOf(tops.find(t => t.length === 96 && t.depth === 48) || tops[0]);
  const cands = tops.filter(t => sizeKeyOf(t) === st.sizeKey);
  if (!DEV) {
    const want = st.multi ? cands.find(t => t.multiFinish) : cands.find(t => !t.multiFinish && t.pieces === st.pieces);
    const t = want || cands.filter(t => !t.multiFinish).sort((a, b) => a.pieces - b.pieces)[0] || cands[0];
    st.pieces = t.pieces; st.multi = t.multiFinish; st.laminate = 'tfl'; st.edge = 'A1'; st.rounded = false;
  }
  if (!cands.some(t => t.pieces === st.pieces && t.multiFinish === st.multi)) { const t = cands[0]; st.pieces = t.pieces; st.multi = t.multiFinish; }
  const top = currentTop();
  if (top.multiFinish && st.laminate === 'hpl') st.laminate = 'tfl';
  const hasPrice = t => Object.values(t || {}).some(v => v != null);
  if (st.laminate === 'tfl' && !hasPrice(top.tfl) && hasPrice(top.hpl)) st.laminate = 'hpl';
  if (st.laminate === 'hpl' && !hasPrice(top.hpl) && hasPrice(top.tfl)) st.laminate = 'tfl';
  const table = st.laminate === 'hpl' ? top.hpl : top.tfl;

  if (woodEdge(st.edge) && (st.finish?.solid || (st.multi && st.midFinish?.solid))) st.edge = 'A1';
  if (table[st.edge] == null) st.edge = Object.keys(table).find(k => table[k] != null) || 'A1';
  if (!st.finish) st.finish = fin('California Walnut');
  if (!st.midFinish) st.midFinish = fin('Glacier White');
  if (!st.baseFinish || st.baseLinked !== false) st.baseFinish = st.finish;
  if (!['square', 'rectangular', 'sightline'].includes(top.shape)) st.rounded = false;

  const fams = familiesFor(top);
  if (!fams.includes(st.famCode)) st.famCode = fams[0];
  const rows = compatibleBases(CAT, top, st.famCode);

  const byHeight = rows.filter(r => !r.height || (st.height === 'standing' ? r.height > 35 : r.height <= 35));
  if (!rows.some(r => r.sku === st.baseSku) || (rows.find(r => r.sku === st.baseSku)?.height && !byHeight.some(r => r.sku === st.baseSku)))
    st.baseSku = (byHeight[0] || rows[0])?.sku || null;
  const fam = CAT.families[st.famCode];
  if (!fam.kick) st.kick = 'BK';
  st.baseOpts['OP-CONF-71'] = !!(fam.kick && st.kick !== 'BK');
  if (fam.family !== 'malibu') { st.baseOpts['OP-CONF-SVP'] = false; st.sideFinish = null; }
  else if (st.baseOpts['OP-CONF-SVP'] == null) st.baseOpts['OP-CONF-SVP'] = false;
  if (st.baseOpts['OP-CONF-SVP']) st.sideFinish = null;

  else if (fam.family === 'malibu' && !st.sideFinish && !st.sideMatch) st.sideFinish = fin('Glacier White');
  if (fam.metal) { const codes = fam.colors ? Object.keys(fam.colors) : ['SV', 'BK', 'WT']; if (!codes.includes(st.metalCode)) st.metalCode = codes[0]; }
  st.baseOpts['OP-CONF-NOTCH'] = false;

  let row = rows.find(r => r.sku === st.baseSku) || null;
  st.grommet.qty = st.grommet.sku ? Math.max(0, Math.min(st.grommet.qty || 0, maxGrommets(top, st.famCode, row, rows))) : 0;
  if (!st.grommet.qty) st.grommet.sku = st.grommet.sku && maxGrommets(top, st.famCode, row, rows) ? st.grommet.sku : '';

  if (PEDESTAL[st.famCode] === 'power' && row) {
    const need = st.grommet.qty > 0 && overColumns(top, row, fam, grommetXs(top, st.grommet.qty)) > 0;
    const swap = need && !isPowerSku(row.sku) ? rows.find(r => r.sku === powerSkuOf(row.sku))
      : !need && isPowerSku(row.sku) && st.powerAuto ? rows.find(r => r.sku === regularSkuOf(row.sku)) : null;
    if (swap) { st.baseSku = swap.sku; st.powerAuto = need; row = swap; }
  }

  let plateSlots = PLATE_GROMMETS.includes(st.grommet.sku) ? st.grommet.qty : 0;
  for (const a of CAT.accessories.filter(a => a.group === 'plate')) {
    const q = Math.min(st.acc[a.sku] || 0, plateSlots); plateSlots -= q;
    if (q > 0) st.acc[a.sku] = q; else delete st.acc[a.sku];
  }

  const bsku = Object.keys(st.acc).find(k => k.startsWith('SY-WMBT'));
  if (bsku) st.acc[bsku] = Math.min(st.acc[bsku] || 0, basketCap(top, row, fam, basketLen(bsku)));
  const spine = CAT.accessories.find(a => a.group === 'spine');
  if (spine && st.acc[spine.sku] > 0) st.acc[spine.sku] = Math.min(st.acc[spine.sku], spineSpots(top, row, fam).length);
  // credenza: a family with a 3-D part, a size the book sells (2-bay widths), one or two units
  if (!st.credenza || typeof st.credenza !== 'object') st.credenza = { type: '', depth: 24, width: 72, qty: 1, locks: false, place: 'back' };
  if (!credPlaces()[st.credenza.place]) st.credenza.place = st.credenza.place === 'left' || st.credenza.place === 'right' ? 'right' : 'back';   // an end -> Front/Back, a long side -> Side
  const cz = st.credenza, cfam = CAT.credenzas?.find(c => c.code === cz.type && c.modelled);
  if (!cfam) cz.type = '';
  else {
    const ok = cfam.sizes;   // every size in the book has a model now (the 36" BCRZ included)
    if (!ok.some(z => z.depth === cz.depth)) cz.depth = 24;
    if (!ok.some(z => z.depth === cz.depth && z.width === cz.width)) cz.width = (ok.find(z => z.depth === cz.depth && z.width === 72) || ok.filter(z => z.depth === cz.depth).pop()).width;
    cz.qty = Math.max(1, Math.min(2, +cz.qty || 1)); cz.locks = !!cz.locks;
  }
  return top;
}

function maxGrommets(top, code, row, rows) {
  let n = top.shape === 'round' || top.shape === 'square' ? 1 : top.pieces >= 3 ? 3 : top.pieces === 1 && top.length <= 72 ? 1 : 2;
  const p = PEDESTAL[code];
  const blocked = p === 'none' || (p === 'power' && row && !isPowerSku(row.sku) && !rows.some(r => r.sku === powerSkuOf(row.sku)));
  if (blocked) while (n > 0 && overColumns(top, row, CAT.families[code], grommetXs(top, n))) n--;
  return n;
}
function gmaxNow(top) { const rows = compatibleBases(CAT, top, st.famCode); return maxGrommets(top, st.famCode, rows.find(r => r.sku === st.baseSku) || null, rows); }

let buildSeq = 0;
async function rebuild() {
  const top = validate();
  const seq = ++buildSeq;
  const fam = CAT.families[st.famCode];
  const row = compatibleBases(CAT, top, st.famCode).find(r => r.sku === st.baseSku) || null;
  const thickness = CAT.edges[st.edge].thickness;
  const tableH = (row?.height ? row.height + thickness : TABLE_H) + (st.reveal ? 0.5 : 0);
  const cfg = {
    edge: st.edge, edgeKind: CAT.edges[st.edge].edge, rounded: st.rounded, finish: st.finish, midFinish: st.multi ? st.midFinish : null,
    tableHeight: tableH, thickness, reveal: st.reveal, revealColor: { SV: '#b9bcc0', WT: '#ececec', BK: '#1c1c1e', PVC: st.finish?.hex || '#1c1c1e' }[st.revealColor] || '#1c1c1e',
  };
  const gcut = GROMMET_CUT[st.grommet.sku] || [8.4, 4.8];
  cfg.cutouts = grommetXs(top, st.grommet.qty).map(x => ({ x, z: 0, w: gcut[0], d: gcut[1], rot: (st.grommet.rot || 0) * Math.PI / 180 }));
  const g = new THREE.Group();

  const topPart = partFor('tops', top.shape);
  let topGroup;
  if (topPart) {
    topGroup = await glbPart(topPart, { x: top.length, y: thickness, z: top.depth }, { top: st.finish });
    topGroup.position.y = tableH - thickness;
    topGroup.userData.bounds = { underside: tableH - thickness };
  } else topGroup = buildTop(top, cfg);
  g.add(topGroup);
  g.add(buildGrommets(top, cfg, st.grommet.qty, st.grommet.color, st.grommet.rot, { sku: st.grommet.sku, cut: gcut }));
  {
    const bsku = Object.keys(st.acc).find(k => k.startsWith('SY-WMBT') && st.acc[k] > 0);
    const slots = bsku ? basketSlots(top, row, fam, basketLen(bsku), grommetXs(top, st.grommet.qty), st.acc[bsku]) : [];
    g.add(buildAccessories(st.acc, CAT, { xs: slots.map(s => s.x), zs: slots.map(s => s.z), z: 0 }, top, topGroup.userData.bounds.underside, spineSpots(top, row, fam)));
  }

  if (row) {
    const bcfg = {
      finish: st.baseFinish, sideFinish: st.sideFinish || st.baseFinish, sideMetal: !!st.baseOpts['OP-CONF-SVP'],
      metalCode: st.metalCode, kickColor: kickHex(), underside: topGroup.userData.bounds.underside, placement: CAT.placement, famCode: st.famCode,
    };
    const basePart = partFor('bases', st.famCode);
    if (basePart) {
      const centers = baseCenters(row, fam, top, CAT.placement);
      for (let i = 0; i < centers.length; i++) {
        const w = orderedWidths(row, top, centers.length)[i];
        const b = await glbPart(basePart, { x: fam.depth ?? w, y: bcfg.underside, z: w }, { base: st.baseFinish, side: bcfg.sideFinish, sideMetal: bcfg.sideMetal, metalCode: st.metalCode });
        b.position.x = centers[i]; g.add(b);
      }
    } else g.add(buildBaseSet(row, fam, top, bcfg));
  }
  // ---- credenza(s): parallel to the side it stands at, CREDENZA_CLEAR away, doors toward the table; two units stand
  // joined side by side. The part's doors face +z: behind = as is, in front = turned round, the ends = a quarter turn.
  const cg = new THREE.Group();
  if (st.credenza?.type) {
    const cz = st.credenza, qty = cz.qty || 1, place = cz.place || 'back';
    const off = (place === 'left' || place === 'right' ? top.length : top.depth) / 2 + CREDENZA_CLEAR + cz.depth / 2;
    const at = { back: [0, -off, 0], front: [0, off, Math.PI], left: [-off, 0, Math.PI / 2], right: [off, 0, -Math.PI / 2] }[place];
    for (let i = 0; i < qty; i++) {
      const c = await credenzaPart(cz.type, { width: cz.width, depth: cz.depth, flush: qty > 1, locks: cz.locks }, credFinish(), 'SV');
      if (!c) break;
      c.position.x = qty > 1 ? (i - (qty - 1) / 2) * cz.width : 0; cg.add(c);
    }
    cg.position.set(at[0], 0, at[1]); cg.rotation.y = at[2];
    g.add(cg);
  }
  if (QS.get('thumb') === 'credenza') { for (const o of [...g.children]) if (o !== cg) g.remove(o); }   // tools/make_credenza_thumbs.py: the credenza alone
  if (seq !== buildSeq) return;   // a newer rebuild superseded this one
  scene.remove(table); disposeGroup(table); table = g; scene.add(table);
  topRef = topGroup; credRef = cg.children.length ? cg : null; if (xray) applyXray(topGroup, true);
  $('#print-shot').removeAttribute('src');
  if (QS.get('thumb') === 'credenza') frameCredenza(true); else frameTable(true);
  $('#hud-size').textContent = `${sizeLabel(top)}${row ? ' · ' + fam.name : ''} · ${seatsShort(top)}`;
  applyViewParam();
}
// the size chips and the popular-table cards use the same seat rule as the seats note (seatsFor, below)
const seatEstimate = t => seatsFor(t)?.comfort ?? null;
function sizeLabel(t) { return (t.shape === 'round' || t.shape === 'square' ? `${t.depth}" ${t.shapeLabel}` : `${t.depthMin !== t.depth ? t.depthMin + '–' : ''}${t.depth}" × ${t.length}" ${t.shapeLabel}`) + ` (${ftDims(t)})`; }

const ft = n => String(+(n / 12).toFixed(2));
function ftDims(t) {
  return t.shape === 'round' || t.shape === 'square' ? `${ft(t.depth)} ft` : `${t.depthMin !== t.depth ? ft(t.depthMin) + '-' : ''}${ft(t.depth)} × ${ft(t.length)} ft`;
}

let viewApplied = false;
function applyViewParam() {
  const v = new URLSearchParams(location.search).get('view'); if (!v || viewApplied) return; viewApplied = true;
  const bb = new THREE.Box3().setFromObject(table), ctr = bb.getCenter(new THREE.Vector3());
  if (v === 'foot') {
    const tgt = new THREE.Vector3(ctr.x, 3, ctr.z);
    controls.target.copy(tgt); camera.position.copy(tgt).add(new THREE.Vector3(1, 0.3, 0.75).normalize().multiplyScalar(62)); controls.update(); return;
  }
  if (v === 'edge') {
    const tgt = new THREE.Vector3(bb.max.x, bb.max.y - 0.6, bb.max.z);
    controls.target.copy(tgt); camera.position.copy(tgt).add(new THREE.Vector3(1.1, 0.42, 1).normalize().multiplyScalar(30)); controls.update(); return;
  }

  if (v === 'credenza') return frameCredenza(false);
  const dirs = { end: [1, 0.12, 0], side: [0, 0.12, 1], top: [0.001, 1, 0], iso: [0.6, 0.45, 0.6], base: [0.8, 0.2, 1], card: [0.62, 0.36, 1], under: [0.8, -0.35, 0.8] };
  const dir = new THREE.Vector3(...(dirs[v] || dirs.iso)).normalize();
  controls.target.copy(ctr);
  camera.position.copy(ctr).add(dir.multiplyScalar(fitDistance(bb, dir))); controls.update();
}
function kickHex() { return { BK: '#111', SV: '#b9bcc0', WT: '#ececec', PVC: st.baseFinish?.hex || '#111' }[st.kick] || '#111'; }
function disposeGroup(g) { g.traverse(o => { if (o.isMesh) { o.geometry?.dispose(); [].concat(o.material, o.userData.solidMat || []).forEach(m => { m?.map?.dispose?.(); m?.dispose?.(); }); } }); }

let xray = false, xrayPick = null, topRef = null, credRef = null;   // credRef: the credenza group, framed on its step
const underTable = () => Object.entries(st.acc).some(([k, q]) => q > 0 && CAT?.accessories.some(a => a.sku === k && a.group !== 'plate'));
function applyXray(group, on) {
  group?.traverse(o => {
    if (!o.isMesh) return;
    if (on && !o.userData.solidMat) {
      o.userData.solidMat = o.material;
      o.material = [].concat(o.material).map(m => Object.assign(m.clone(), { transparent: true, opacity: 0.18, depthWrite: false }));
      if (!Array.isArray(o.userData.solidMat)) o.material = o.material[0];
    } else if (!on && o.userData.solidMat) {
      [].concat(o.material).forEach(m => m.dispose());
      o.material = o.userData.solidMat; delete o.userData.solidMat;
    }
  });
}
function setXray(on) {
  xray = !!on; applyXray(topRef, xray);
  document.querySelectorAll('#xray-toggle [data-xray]').forEach(b => { const a = (b.dataset.xray === '1') === xray; b.classList.toggle('is-on', a); b.setAttribute('aria-pressed', String(a)); });
}
document.querySelectorAll('#xray-toggle [data-xray]').forEach(b => b.addEventListener('click', () => { xrayPick = b.dataset.xray === '1'; setXray(xrayPick); telemetry.track('xray_toggle', { on: xrayPick }); }));

const SHAPE_ICONS = {
  round: '<svg viewBox="0 0 56 26"><circle cx="28" cy="13" r="11" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
  square: '<svg viewBox="0 0 56 26"><rect x="17" y="2" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
  rectangular: '<svg viewBox="0 0 56 26"><rect x="4" y="6" width="48" height="14" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
  racetrack: '<svg viewBox="0 0 56 26"><rect x="4" y="6" width="48" height="14" rx="7" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
  boat: '<svg viewBox="0 0 56 26"><path d="M5 8 Q28 -1 51 8 L51 18 Q28 27 5 18 Z" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
  bowend: '<svg viewBox="0 0 56 26"><path d="M7 6 L49 6 Q55 13 49 20 L7 20 Q1 13 7 6 Z" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
  sightline: '<svg viewBox="0 0 56 26"><path d="M4 3 L52 9 L52 17 L4 23 Z" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
};

function anyTopPrice(t) {
  const cfg = { laminate: st.laminate, edge: st.edge, finish: st.finish, midFinish: st.midFinish };
  return topPrice(t, cfg) ?? topPrice(t, { ...cfg, laminate: 'tfl', edge: 'A1' });
}
const fromPrice = (vals, pre = 'from ') => { const v = vals.filter(x => x != null); return v.length ? price(`<small>${v.length > 1 && Math.min(...v) !== Math.max(...v) ? pre : ''}${money(Math.min(...v))}</small>`) : ''; };
function buildShapeChips() {
  const box = $('#shape-chips'); box.innerHTML = '';
  for (const [shape, label] of Object.entries(CAT.shapes)) {
    const c = el('button', 'chip' + (st.shape === shape ? ' active' : ''), `<span class="ico">${SHAPE_ICONS[shape]}</span>${label}${fromPrice(CAT.tops.filter(t => t.shape === shape && shippable(t)).map(anyTopPrice))}`);
    c.addEventListener('click', () => { st.shape = shape; update(); });
    box.appendChild(c);
  }
}
// ---------------------------------------------------------------- seats (Jeremy, 2026-10-08: "show the estimated
// amount of people based on table size"). The same rules as the conference room size calculator
// (assets/os-conference-calc.js) so the two never disagree: laptop room = 30" of edge per person plus one seat at each
// end when that end is at least 42" deep; shoulder to shoulder = 24" per person along the long sides only. Round tops
// use the circumference. Squares count all four sides (the calculator's published 42" square = 4 either way).
const SEATS = { comfort: 30, board: 24, endMin: 42 };
// an end seat needs that END to be 42" deep: a boat top narrows to depthMin at both ends, a sightline top is wide at
// one end (depth) and narrow at the other (depthMin, the screen end); every other long shape keeps its depth
const endSeats = t => { const lo = t.depthMin || t.depth, ok = d => (d >= SEATS.endMin ? 1 : 0);
  return t.shape === 'boat' ? 2 * ok(lo) : t.shape === 'sightline' ? ok(t.depth) + ok(lo) : 2 * ok(t.depth); };
function seatsFor(t) {
  if (!t) return null;
  const at = (s, ends) => t.shape === 'round' ? Math.floor(Math.PI * t.depth / s)
    : t.shape === 'square' ? 4 * Math.floor(t.depth / s)
    : Math.floor(t.length / s) * 2 + (ends ? endSeats(t) : 0);
  const comfort = at(SEATS.comfort, true);
  return { comfort, board: Math.max(comfort, at(SEATS.board, false)) };
}
const seatsShort = t => { const s = seatsFor(t); return s ? `seats ${s.comfort === s.board ? s.comfort : s.comfort + '–' + s.board}` : ''; };
function paintSeats(top) {
  const box = $('#seats-note'); if (!box) return;
  const s = seatsFor(top); if (!s) { box.hidden = true; return; }
  box.hidden = false;
  const round = top.shape === 'round' || top.shape === 'square';
  box.innerHTML = `<span class="seats__icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><circle cx="9" cy="8" r="3"/><path d="M3.5 19c.6-3 2.8-5 5.5-5s4.9 2 5.5 5"/><circle cx="17" cy="9" r="2.4"/><path d="M15.5 14.2c2.4-.3 4.5 1.4 5 4.8"/></svg></span>`
    + `<span><b>Seats ${s.comfort}</b> with laptop room${round ? '' : ' (30" each, one at each end)'}`
    + (s.board > s.comfort ? `, <b>up to ${s.board}</b> shoulder to shoulder` : '') + '.'
    + ` <a class="textlink" href="/pages/conference-table-size-calculator" target="_blank" rel="noopener">Will it fit your room?</a></span>`;
}

function buildSizeUI(top) {
  const sel = $('#sel-size'); sel.innerHTML = '';
  const seen = new Map();
  for (const t of topsForShape()) { const k = sizeKeyOf(t); if (!seen.has(k)) seen.set(k, t); }
  for (const [k, t] of seen) {
    const same = topsForShape().filter(x => sizeKeyOf(x) === k);
    const match = same.find(x => x.pieces === st.pieces && x.multiFinish === st.multi);
    const p = (match && anyTopPrice(match)) ?? Math.min(...same.map(anyTopPrice).filter(v => v != null));
    const o = el('option', null, (t.shape === 'round' ? `${t.depth}" round` : t.shape === 'square' ? `${t.depth}" square` : `${t.depthMin !== t.depth ? t.depthMin + '-' : ''}${t.depth}"D × ${t.length}"L`) + ` (${ftDims(t)})` + ' · ' + seatsShort(t) + (p != null && isFinite(p) ? price(' · ' + money(p)) : ''));
    o.value = k; if (k === st.sizeKey) o.selected = true; sel.appendChild(o);
  }
  sel.onchange = () => { st.sizeKey = sel.value; update(); };
  paintSeats(top);
  // phones: the sizes as a tap grid (the dropdown stays for desktop and for the long tail behind "More sizes")
  const sc = $('#size-chips');
  if (sc) {
    sc.innerHTML = ''; sc.hidden = false;
    const keys = [...seen.keys()], cap = 8, more = keys.length > cap + 1 && !sc.dataset.all;
    const shown = more ? keys.filter((k, i) => i < cap || k === st.sizeKey) : keys;
    for (const k of shown) {
      const t = seen.get(k), n = seatEstimate(t);
      const lbl = t.shape === 'round' ? `${t.depth}" round` : t.shape === 'square' ? `${t.depth}" sq` : `${t.depth} × ${t.length}`;
      const c = el('button', 'chip size-chip' + (k === st.sizeKey ? ' active' : ''), `${lbl}${n ? `<small>seats ${n}</small>` : ''}`);
      c.type = 'button'; c.addEventListener('click', () => { st.sizeKey = k; update(); });
      sc.appendChild(c);
    }
    if (more) { const m = el('button', 'chip size-chip size-chip--more', `More sizes<small>${keys.length - shown.length} more</small>`); m.type = 'button'; m.addEventListener('click', () => { sc.dataset.all = '1'; buildSizeUI(top); }); sc.appendChild(m); }
  }

  const pc = $('#pieces-chips'); pc.innerHTML = '';
  const cands = topsForShape().filter(t => sizeKeyOf(t) === st.sizeKey);
  for (const t of cands.sort((a, b) => a.pieces - b.pieces || a.multiFinish - b.multiFinish)) {
    const pp = anyTopPrice(t);
    const lbl = (t.multiFinish ? '3 · multi-finish' : `${t.pieces} piece${t.pieces > 1 ? 's' : ''}`) + (pp != null ? price(`<small>${money(pp)}</small>`) : '');
    const c = el('button', 'chip' + (t.pieces === st.pieces && t.multiFinish === st.multi ? ' active' : ''), lbl);
    c.addEventListener('click', () => { st.pieces = t.pieces; st.multi = t.multiFinish; update(); });
    pc.appendChild(c);
  }
  const lc = $('#lam-chips'); lc.innerHTML = '';
  for (const [k, lbl0] of [['tfl', 'TFL (thermofused)'], ['hpl', 'HPL (high pressure)']]) {
    const lp = (top[k] || {})[st.edge] ?? (top[k] || {}).A1;
    const lbl = lbl0 + (lp != null ? price(`<small>${money(lp)}${k === 'tfl' && st.finish?.premium ? ' +10%' : ''}</small>`) : '');
    const c = el('button', 'chip' + (st.laminate === k ? ' active' : ''), lbl);
    if (k === 'hpl' && top.multiFinish) c.disabled = true;
    if (!Object.values(top[k] || {}).some(v => v != null)) { c.disabled = true; c.title = 'Not offered in ' + lbl + ' at this size'; }
    c.addEventListener('click', () => { st.laminate = k; update(); });
    lc.appendChild(c);
  }

  const table = st.laminate === 'hpl' ? top.hpl : top.tfl;
  const avail = Object.keys(CAT.edges).filter(k => table[k] != null), thickOf = k => CAT.edges[k].thickness;
  const inch = v => { const w = Math.floor(v), e = Math.round((v - w) * 8), [a, b] = e % 4 === 0 ? [e / 4, 2] : e % 2 === 0 ? [e / 2, 4] : [e, 8]; return `${w || ''}${e ? `${w ? '-' : ''}${a}/${b}` : ''}"`; };
  const order = [1.125, 1.875, 1];
  const thicks = [...new Set(avail.map(thickOf))].sort((a, b) => (order.indexOf(a) + 1 || 9) - (order.indexOf(b) + 1 || 9));
  const curT = thickOf(st.edge);
  const tc = $('#thick-chips'); tc.innerHTML = '';
  for (const t of thicks) {
    const codes = avail.filter(k => thickOf(k) === t), from = Math.min(...codes.map(k => table[k]));
    const c = el('button', 'chip' + (t === curT ? ' active' : ''), `${inch(t)} thick${t === 1.125 ? ' (standard)' : ''}${price(`<small>${codes.length > 1 ? 'from ' : ''}${money(from)}</small>`)}`);
    c.addEventListener('click', () => {
      if (t === curT) return;
      const kind = CAT.edges[st.edge].edge;
      st.edge = codes.find(k => CAT.edges[k].edge === kind) || codes.find(k => CAT.edges[k].edge === 'pvc') || codes[0]; update();
    });
    tc.appendChild(c);
  }
  const es = $('#edge-chips'); es.innerHTML = '';
  const edgesHere = avail.filter(k => thickOf(k) === curT);
  for (const code of edgesHere) {
    const e = CAT.edges[code], [band, rest = ''] = e.label.split(' · ');
    const extra = rest.replace(/^[\d\-\/"]+\s*(thick)?\s*/, '');
    const c = el('button', 'chip edge' + (code === st.edge ? ' active' : ''), `${edgeIcon(code, st.finish?.hex)}<span class="lbl">${band}<small>${[extra, price(money(table[code]))].filter(Boolean).join(' · ')}</small></span>`);
    c.title = EDGE_PROFILES[code]?.blurb || '';
    if (e.edge === 'wood' && (st.finish?.solid || (st.multi && st.midFinish?.solid))) { c.disabled = true; c.title = 'Solid wood edges are not available in white, black or solid gray.'; }
    c.addEventListener('click', () => { st.edge = code; update(); });
    es.appendChild(c);
  }
  $('#edge-hint').textContent = (EDGE_PROFILES[st.edge]?.blurb || '') + (edgesHere.length === 1 ? ` ${inch(curT)} tops come with this edge only.` : '');
  const tt = $('#tone-chips') || (() => { const r = el('div', 'field', '<span class="field__label">Finish style</span><div class="chips" id="tone-chips"></div>'); $('#sel-size').closest('.field').after(r); return r.querySelector('#tone-chips'); })();
  tt.innerHTML = '';
  const hasMulti = cands.some(t => t.multiFinish);
  tt.closest('.field').style.display = hasMulti && !DEV ? '' : 'none';
  if (hasMulti) for (const [flag, lbl] of [[false, 'One finish'], [true, 'Two-tone (ends / middle)']]) {
    const c = el('button', 'chip' + (st.multi === flag ? ' active' : ''), lbl); c.addEventListener('click', () => { st.multi = flag; update(); }); tt.appendChild(c);
  }
  const hplNote = st.laminate === 'hpl' ? ' HPL prices shown are for standard HPL colors.' : '';
  $('#top-hint').textContent = (top.pieces > 1
    ? `${top.pieces}-piece top: the sections join underneath with connector hardware, and the grain does not line up across the seam${top.pieces > 2 ? 's' : ''}.`
    : 'Grain runs the length of the table.') + hplNote;
}
function buildBaseUI(top) {
  const grid = $('#base-chips'); grid.innerHTML = '';
  for (const code of familiesFor(top)) {
    const f = CAT.families[code];
    const rows = compatibleBases(CAT, top, code).filter(r => !r.height || (st.height === 'standing' ? r.height > 35 : r.height <= 35));
    const from = Math.min(...(rows.length ? rows : compatibleBases(CAT, top, code)).map(r => rowPrice(r, top)));
    const c = el('button', 'base-card' + (code === st.famCode ? ' active' : ''), `<span class="thumb"><img src="${assetUrl(`assets/base-thumbs/${code}.png${relQ}`)}" alt="" loading="lazy"></span><span class="name">${f.name}${price(`<small>${rows.length > 1 ? 'from ' : ''}${money(from)}</small>`)}</span>`);
    c.querySelector('img').addEventListener('error', e => (e.target.style.visibility = 'hidden'));
    c.addEventListener('click', () => { st.famCode = code; st.baseSku = null; update(); });
    grid.appendChild(c);
  }

  const rowsAll = compatibleBases(CAT, top, st.famCode);
  const hasStanding = rowsAll.some(r => r.height > 35), hasSeated = rowsAll.some(r => r.height && r.height <= 35);
  let hc = $('#height-chips');
  if (!hc) { const r = el('div', 'field', '<span class="field__label">Height</span><div class="chips" id="height-chips"></div>'); grid.after(r); hc = r.querySelector('#height-chips'); }
  hc.closest('.field').style.display = hasStanding && hasSeated ? '' : 'none';
  hc.innerHTML = '';
  for (const [k, lbl0] of [['seated', 'Seated · 29"'], ['standing', 'Standing · 42"']]) {
    const lbl = lbl0 + fromPrice(rowsAll.filter(r => r.height && (k === 'standing' ? r.height > 35 : r.height <= 35)).map(r => rowPrice(r, top)));
    const c = el('button', 'chip' + (st.height === k ? ' active' : ''), lbl); c.addEventListener('click', () => { st.height = k; st.baseSku = null; update(); }); hc.appendChild(c);
  }
  const bs = $('#sel-base'); bs.innerHTML = '';
  for (const r of compatibleBases(CAT, top, st.famCode)) {
    const w = r.widths.length > 1 && !r.single ? r.widths.join('/') : r.widths[0];

    const o = el('option', null, `${r.bases} base${r.bases > 1 ? 's' : ''} · ${w}" wide${r.stretcher ? ` · ${r.stretcher}" stretcher` : ''}${r.height ? ` · ${r.height > 35 ? 'standing' : 'seated'} height` : ''}${isPowerSku(r.sku) ? ' · power model, cords run down the column' : ''}${price(' · ' + money(r.price))}`);
    o.value = r.sku; if (r.sku === st.baseSku) o.selected = true; bs.appendChild(o);
  }
  bs.onchange = () => {
    const r = compatibleBases(CAT, top, st.famCode).find(x => x.sku === bs.value);
    st.baseSku = bs.value; st.powerAuto = false; if (r?.height) st.height = r.height > 35 ? 'standing' : 'seated'; update();
  };
  bs.closest('.field').style.display = bs.options.length > 1 ? '' : 'none';
  const fam = CAT.families[st.famCode];
  const hints = {
    malibu: 'Hollow 3/4" laminate bases, sold as a set with wire-management troughs. The side panels match the base unless you pick another laminate or silver metallic in step 4, Finishes.',
    torrance: 'Two 1-1/8" laminate panels with a 1/8" reveal and a recessed kickplate. Solid, so wires cannot route through.',
    imperial: 'Boat-shaped 9" HPL base, hollow for wires, 1-1/8" kickplate. HPL only.',
    komo: 'HPL-wrapped hollow cylinders with a 1-1/8" kickplate. 18"/24" cylinders need 50 lb of ballast for stability.',
    hermosa: 'Laminate X-base for small square and round tops. Solid, no wire routing.',
    ribbon: 'Open-frame steel legs with spanning rods; powdercoat silver, black or white. Made in America.',
    surfside: 'Open-frame 1"×2-1/2" steel legs with spanning rods; silver, black or white powdercoat. Wire-management sets put a 12"-deep laminate pedestal with hinged doors in each middle position (end legs stay metal).',
    sunset: 'A-frame steel legs (narrower at top) with spanning rods; silver, black or white powdercoat. Wire-management sets put a 12"-deep laminate pedestal with hinged doors in each middle position (end legs stay metal).',
    beacon: 'Single round disc base with center column; 27-3/4" (seated) or 40-3/4" (standing) height.',
    mission: 'Single square disc base with center column; 27-3/4" (seated) or 40-3/4" (standing) height.',
  };
  $('#base-hint').textContent = hints[fam.family] || '';
}
function swatches(host, active, onPick, priceOf, lam = 'TFL') {
  const box = host.querySelector('.swatches'); box.innerHTML = '';
  const groups = [[`Standard ${lam}`, CAT.finishes.filter(f => !f.premium)], [`Premium ${lam}`, CAT.finishes.filter(f => f.premium)]];
  for (const [label, list] of groups) {
    box.appendChild(el('div', 'swgroup', `<span class="tag${label.startsWith('Premium') ? ' prem' : ''}">${label}</span>`));
    for (const f of list) {
      const b = el('button', 'swatch' + (f.premium ? ' premium' : '') + (active && active.name === f.name ? ' active' : ''));
      b.title = (f.premium ? 'Premium ' : f.solid ? 'Solid ' : 'Standard ') + lam;
      const bg = f.hires ? `background:url('${encodeURI(assetUrl('assets/swatches_hires/' + f.hires))}') center/cover, ${f.hex}` : `background:${f.hex}`;
      const cap = priceOf ? priceOf(f) : '';
      b.innerHTML = `<span class="chipc" style="${bg}"></span><span class="name">${f.name}${cap ? `<small>${cap}</small>` : ''}</span>`;
      b.addEventListener('click', () => onPick(f));
      box.appendChild(b);
    }
  }
}

let finPart = 'top';
function showFinPart(k) {
  finPart = k;
  if (tab === 'finish') telemetry.part(k);
  document.querySelectorAll('#fin-tabs .subtab').forEach(b => { const on = b.dataset.part === k; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
  document.querySelectorAll('#g-finish .fin-part').forEach(p => (p.hidden = p.dataset.part !== k));
  if (tab === 'finish') navLabels();
}
function buildFinishUI(top) {
  const fam = CAT.families[st.famCode];

  const topLam = st.laminate === 'hpl' ? 'HPL' : 'TFL';
  const baseLam = HPL_ONLY_BASES.includes(fam.family) ? 'HPL' : topLam;
  const baseTakesFinish = !fam.metal || fam.wirebox;
  const topCap = f => f.premium ? price(baseTakesFinish && st.baseLinked ? '+10% on top and base' : '+10%') : '';
  const baseCap = f => f.premium ? price('+10%') : '';
  const hasSides = fam.family === 'malibu', svp = !!st.baseOpts['OP-CONF-SVP'];
  if (finPart === 'side' && !hasSides) finPart = 'top';
  const metalName = fam.metal ? ((fam.colors || {})[st.metalCode] || METALS[st.metalCode]?.name || '').replace('*', '') : '';
  const parts = [
    ['top', 'Top', top.multiFinish ? `${st.finish.name} / ${st.midFinish.name}` : st.finish.name],
    ['base', 'Base', !fam.metal ? (st.baseLinked ? 'Matches the top' : st.baseFinish.name)
      : fam.wirebox ? `${metalName} legs, ${st.baseLinked ? 'pedestal matches the top' : st.baseFinish.name + ' pedestal'}` : metalName],
  ];
  if (hasSides) parts.push(['side', 'Base side panels', svp ? 'Silver metallic' : st.sideFinish ? st.sideFinish.name : 'Matches the base']);
  const tabs = $('#fin-tabs'); tabs.innerHTML = ''; tabs.style.setProperty('--n', parts.length);
  for (const [k, name, val] of parts) {
    const b = el('button', 'subtab', `<span class="subtab__name">${name}</span><span class="subtab__val">${val}</span>`);
    b.type = 'button'; b.dataset.part = k; b.addEventListener('click', () => showFinPart(k));
    tabs.appendChild(b);
  }
  showFinPart(finPart);
  swatches($('#fin-top'), st.finish, f => { st.finish = f; update(); }, topCap, topLam);
  $('#fin-mid').hidden = !top.multiFinish;
  if (top.multiFinish) swatches($('#fin-mid'), st.midFinish, f => { st.midFinish = f; update(); }, topCap, topLam);
  $('#fin-base').hidden = !!fam.metal && !fam.wirebox;
  if (!fam.metal || fam.wirebox) {
    swatches($('#fin-base'), st.baseFinish, f => { st.baseFinish = f; st.baseLinked = f.name === st.finish?.name; update(); }, baseCap, baseLam);

    const h = $('#fin-base h3');
    h.innerHTML = st.baseLinked ? 'Base <span class="fin-note">matches the top</span>' : 'Base <button class="linkbtn" type="button" id="base-match">Match the top</button>';
    $('#base-match')?.addEventListener('click', () => { st.baseLinked = true; update(); });
  }

  if (hasSides) {
    const side = $('#fin-side');
    swatches(side, svp ? null : (st.sideFinish || st.baseFinish), f => { st.baseOpts['OP-CONF-SVP'] = false; st.sideFinish = f.name === st.baseFinish?.name ? null : f; st.sideMatch = !st.sideFinish; update(); }, null, baseLam);
    const box = side.querySelector('.swatches'), opt = CAT.options.find(o => o.sku === 'OP-CONF-SVP');
    const m = el('button', 'swatch swatch--metal' + (svp ? ' active' : ''), `<span class="chipc"></span><span class="name">Silver metallic${price(`<small>+${money(opt.price)} per base</small>`)}</span>`);
    m.type = 'button'; m.title = 'Silver metallic side panels'; m.addEventListener('click', () => { st.baseOpts['OP-CONF-SVP'] = true; st.sideFinish = null; update(); });
    box.prepend(m); box.prepend(el('div', 'swgroup', '<span class="tag">Metal</span>'));
    side.querySelector('h3').innerHTML = !svp && !st.sideFinish ? 'Base side panels <span class="fin-note">match the base</span>' : 'Base side panels <button class="linkbtn" type="button" id="side-match">Match the base</button>';
    $('#side-match')?.addEventListener('click', () => { st.baseOpts['OP-CONF-SVP'] = false; st.sideFinish = null; st.sideMatch = true; update(); });
  }
  $('#fin-metal').hidden = !fam.metal;
  if (fam.metal) {
    const mc = $('#metal-chips'); mc.innerHTML = '';
    const codes = fam.colors ? Object.keys(fam.colors) : ['SV', 'BK', 'WT'];
    if (!codes.includes(st.metalCode)) st.metalCode = codes[0];
    const rowNow = compatibleBases(CAT, top, st.famCode).find(r => r.sku === st.baseSku);
    for (const code of codes) {
      const prem = fam.premiumColors?.includes(code);
      const lbl = ((fam.colors || {})[code] || METALS[code].name).replace('*', '') + (prem ? price(' +10%') : '');
      const c = el('button', 'chip chip--dot' + (st.metalCode === code ? ' active' : ''), `<span class="dot" style="background:${METALS[code]?.hex || '#999'}"></span>${lbl}`); c.addEventListener('click', () => { st.metalCode = code; update(); }); mc.appendChild(c);
    }
    if (fam.premiumColors?.length && DEV) mc.appendChild(el('p', 'hint', 'Premium colors add 10% to the price of the leg set.'));
  }
  $('#fin-kick').hidden = !fam.kick;
  if (fam.kick) {
    const kc = $('#kick-chips'); kc.innerHTML = '';
    const kp = price(` +${money(CAT.options.find(o => o.sku === 'OP-CONF-71').price)}/base`);
    for (const [code, lbl] of [['BK', 'Black (standard)'], ['SV', 'Silver' + kp], ['WT', 'White' + kp], ['PVC', 'Matching' + kp]]) {
      const c = el('button', 'chip' + (st.kick === code ? ' active' : ''), lbl); c.addEventListener('click', () => { st.kick = code; update(); }); kc.appendChild(c);
    }
  }
}

function buildGrommetCards(gs) {
  const box = $('#grommet-cards'); box.innerHTML = ''; box.classList.toggle('off', gs.disabled);
  for (const op of gs.options) {
    const sku = op.value, info = GROMMET_INFO[sku] || {}, o = CAT.options.find(x => x.sku === sku), on = sku === (st.grommet.sku || '');
    const gp = o ? Object.values(o.colors) : [], from = gp.length ? price(`${Math.min(...gp) !== Math.max(...gp) ? 'from ' : ''}${money(Math.min(...gp))}`) : '';
    const card = el('div', 'gcard' + (sku ? '' : ' gcard--none') + (on ? ' active' : ''));
    card.dataset.sku = sku;
    if (sku) {
      const pic = el('button', 'gcard__img', `<img src="${grommetImg(sku)}" alt="" loading="lazy"><span class="gcard__zoom" aria-hidden="true">Details</span>`);
      pic.type = 'button'; pic.setAttribute('aria-label', `Details: ${info.title || noDash(o.label)}`);
      pic.addEventListener('click', () => openGrommet(sku));
      card.appendChild(pic);
    }
    const pick = el('button', 'gcard__pick', `<b>${sku ? info.title || noDash(o.label) : 'No power grommet'}</b>${from ? `<small>${from}</small>` : ''}`);
    pick.type = 'button'; pick.disabled = gs.disabled && !!sku; pick.setAttribute('aria-pressed', String(on));
    pick.addEventListener('click', () => chooseGrommet(sku));
    card.appendChild(pick); box.appendChild(card);
  }
}
function chooseGrommet(sku) { const gs = $('#sel-grommet'); if (gs.disabled && sku) return; gs.value = sku; gs.onchange(); }

function openGrommet(sku) {
  const o = CAT.options.find(x => x.sku === sku), info = GROMMET_INFO[sku] || {}, spec = CAT.grommetSpecs[sku] || {}, gs = $('#sel-grommet');
  const img = $('#gdlg-img'); img.src = grommetImgLarge(sku); img.alt = info.title || '';
  $('#gdlg-shown').textContent = info.shown ? `Shown in ${info.shown}.` : '';
  $('#gdlg-title').textContent = `Power grommet: ${info.title || noDash(o.label)}`;
  const names = a => a.length > 1 ? `${a.slice(0, -1).join(', ')} or ${a[a.length - 1]}` : a[0];
  const by = {}; for (const [c, p] of Object.entries(o.colors)) (by[p] ||= []).push(CAT.grommetColors[c]);
  $('#gdlg-price').textContent = DEV ? Object.entries(by).sort((a, b) => a[0] - b[0]).map(([p, n]) => `${names(n)}: ${money(+p)} each`).join(' · ')
    : `Finishes: ${names(Object.keys(o.colors).map(c => CAT.grommetColors[c]))}`;
  const size = spec.size && spec.size !== 'recessed' ? `${spec.size}, sits flush in the tabletop` : 'Sits flush in the tabletop';
  $('#gdlg-specs').innerHTML = [size, ...(spec.features || []).map(specLine)].map(x => `<li>${x}</li>`).join('');
  const choose = $('#gdlg-choose'), chosen = st.grommet.sku === sku;
  choose.dataset.sku = sku; choose.disabled = gs.disabled || chosen; choose.textContent = chosen ? 'Chosen' : 'Choose this grommet';
  $('#gdlg-note').hidden = !gs.disabled; $('#gdlg-note').textContent = gs.disabled ? $('#grommet-spec').textContent : '';
  $('#grommet-dialog').showModal();
  telemetry.track('grommet_details', { sku });
}
{
  const dlg = $('#grommet-dialog');
  $('#gdlg-choose').addEventListener('click', e => { chooseGrommet(e.currentTarget.dataset.sku); dlg.close(); });
  $('#gdlg-close').addEventListener('click', () => dlg.close());
  $('#gdlg-back').addEventListener('click', () => dlg.close());
  dlg.addEventListener('click', e => {
    const r = dlg.getBoundingClientRect();
    if (e.target === dlg && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)) dlg.close();
  });
  $('#grommet-spec').addEventListener('click', e => { const b = e.target.closest('[data-gdetails]'); if (b) openGrommet(b.dataset.gdetails); });
}

function stepper(inp) {
  let w = inp.parentElement;
  if (!w.classList.contains('stepper')) {
    w = el('span', 'stepper'); inp.replaceWith(w);
    const name = (inp.getAttribute('aria-label') || 'quantity').toLowerCase();
    const btn = (sign, lbl) => { const b = el('button', 'stepper__btn', sign); b.type = 'button'; b.setAttribute('aria-label', `${lbl}: ${name}`); return b; };
    w.append(btn('−', 'One fewer'), inp, btn('+', 'One more'));
    w.querySelectorAll('.stepper__btn').forEach((b, k) => b.addEventListener('click', () => {
      const lo = +inp.min || 0, hi = inp.max === '' ? Infinity : +inp.max, v = Math.max(lo, Math.min(hi, (+inp.value || 0) + (k ? 1 : -1)));
      if (v !== +inp.value) { inp.value = v; inp.dispatchEvent(new Event('change', { bubbles: true })); }
    }));
  }
  const v = +inp.value || 0, [minus, , plus] = w.children;
  minus.disabled = inp.disabled || v <= (+inp.min || 0); plus.disabled = inp.disabled || (inp.max !== '' && v >= +inp.max);
  return w;
}
function buildOptionsUI(top) {
  const fam = CAT.families[st.famCode];
  const list = $('#opt-list'); list.innerHTML = '';
  const add = (id, label, price, checked, onChange, disabled = false) => {
    const row = el('label', 'opt', `<input type="checkbox" ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}/> <span>${label}</span><span class="price">${price}</span>`);
    row.querySelector('input').addEventListener('change', e => { onChange(e.target.checked); update(); });
    list.appendChild(row);
  };
  if (DEV && ['square', 'rectangular', 'sightline'].includes(top.shape)) add('rnd', 'Rounded corners, 4" radius', '+' + money(CAT.options.find(o => o.sku === 'OP-EDGE-RND').price), st.rounded, v => (st.rounded = v));
  const rv = revealLine(CAT, top);
  if (DEV && rv) add('rev', `1/2" reveal below top`, '+' + money(rv.price), st.reveal, v => (st.reveal = v));
  if (DEV && st.reveal) {
    const rc = el('div', 'field', '<span class="field__label">Reveal color</span><div class="chips" id="reveal-chips"></div>'); list.appendChild(rc);
    for (const [code, lbl] of [['SV', 'Silver'], ['WT', 'White'], ['BK', 'Black'], ['PVC', 'Matching PVC']]) {
      const c = el('button', 'chip' + (st.revealColor === code ? ' active' : ''), lbl); c.addEventListener('click', () => { st.revealColor = code; update(); }); rc.querySelector('#reveal-chips').appendChild(c);
    }
  }
  const row = compatibleBases(CAT, top, st.famCode).find(r => r.sku === st.baseSku);
  for (const o of CAT.options.filter(o => o.scope === 'base' && o.sku !== 'OP-CONF-71' && o.families.includes(fam.family))) {
    if (o.sku === 'OP-CONF-NOTCH') continue;
    if (o.sku === 'OP-CONF-SVP') continue;
    add(o.sku, o.label, price(`+${money(o.price)} × ${row?.bases || 1}`), !!st.baseOpts[o.sku], v => (st.baseOpts[o.sku] = v));
  }

  const door = row && CAT.options.find(o => o.scope === 'door' && o.codes.includes(row.code));
  if (door) {

    const dr = el('div', 'acc', `<span>${door.label}${price(' · ' + money(door.price) + ' each')}</span><input type="number" min="0" max="${row.bases}" value="${Math.min(st.doors || 0, row.bases)}" aria-label="Number of access doors">`);
    dr.querySelector('input').addEventListener('change', e => { st.doors = Math.max(0, Math.min(row.bases, +e.target.value || 0)); update(); });
    stepper(dr.querySelector('input'));
    list.appendChild(dr);
  } else st.doors = 0;

  const gs = $('#sel-grommet'); gs.innerHTML = '';
  gs.appendChild(Object.assign(el('option', null, 'None'), { value: '' }));
  for (const o of CAT.options.filter(o => o.scope === 'grommet')) {
    if (o.sku === 'AC-DPGR-74' && fam.family === 'komo' && row && row.widths[0] === 18) continue;
    const gp = Object.values(o.colors); const gmin = Math.min(...gp);
    const opt = el('option', null, `${noDash(o.label)}${price(` · ${Math.min(...gp) !== Math.max(...gp) ? 'from ' : ''}${money(gmin)}`)}`); opt.value = o.sku; if (o.sku === st.grommet.sku) opt.selected = true; gs.appendChild(opt);
  }
  gs.onchange = () => { st.grommet.sku = gs.value; if (gs.value && !st.grommet.qty) st.grommet.qty = 1; if (!gs.value) st.grommet.qty = 0; update(); };
  const gc = $('#sel-grommet-color'); gc.innerHTML = '';
  const o = CAT.options.find(x => x.sku === st.grommet.sku);
  $('#row-grommet-extra').style.display = o ? '' : 'none';
  if (o) {
    if (!o.colors[st.grommet.color]) st.grommet.color = Object.keys(o.colors)[0];
    for (const [code, pr] of Object.entries(o.colors)) { const op = el('option', null, `${CAT.grommetColors[code]}${price(' · ' + money(pr))}`); op.value = code; if (code === st.grommet.color) op.selected = true; gc.appendChild(op); }
    gc.onchange = () => { st.grommet.color = gc.value; update(); };
    const q = $('#grommet-qty'); q.max = gmaxNow(top); q.value = st.grommet.qty;
    q.onchange = () => { st.grommet.qty = Math.max(0, Math.min(gmaxNow(top), +q.value || 0)); update(); };
    stepper(q);
  }
  const gmax = gmaxNow(top), round = top.shape === 'round' || top.shape === 'square';
  gs.disabled = !gmax;
  buildGrommetCards(gs);
  let where;
  if (!gmax) {
    const ok = compatibleFamilies(CAT, top).filter(c => { const rs = compatibleBases(CAT, top, c); return maxGrommets(top, c, rs[0] || null, rs) > 0; })
      .map(c => CAT.families[c].name + (PEDESTAL[c] === 'power' ? ' (power model)' : ''));
    where = `This base has no path for power cords, so it cannot take a power grommet${ok.length ? `. For power, choose ${ok.slice(0, -1).join(', ')}${ok.length > 1 ? ' or ' : ''}${ok[ok.length - 1]} in step 3.` : '.'}`;
  } else where = round ? 'Round and square tops take one grommet, in the center.'
    : `This top takes ${gmax === 1 ? 'one grommet, in the center' : `up to ${gmax} grommets, spaced along the center line`}.`;
  const notes = [];
  if (st.grommet.qty && isPowerSku(row?.sku) && st.powerAuto) {
    const std = compatibleBases(CAT, top, st.famCode).find(r => r.sku === regularSkuOf(row.sku));
    notes.push(`The grommet sits over the column, so the base is the ${fam.name.replace(/ base$/i, '')} power model, which runs the cords down through the column${std ? price(` (+${money(row.price - std.price)})`) : ''}.`);
  }
  const nn = notchCount(top, row, fam); lastNotchRisk = notchRisk(top, row, fam);
  if (nn) notes.push(`A grommet lands on the solid top of ${nn === 1 ? 'one base' : `${nn} bases`}, so ${nn === 1 ? 'that base gets' : 'those bases get'} notched for it${price(` (+${money(CAT.options.find(x => x.sku === 'OP-CONF-NOTCH').price)} per base)`)}.`);

  const info = o && GROMMET_INFO[o.sku];
  $('#grommet-spec').innerHTML = (info ? `${info.summary} <button class="linkbtn" type="button" data-gdetails="${o.sku}">Details</button><br>` : '') + where
    + notes.map(n => `<br><strong>${n}</strong>`).join('');

  const pl = $('#plate-list'); pl.innerHTML = '';
  const plateSlots = PLATE_GROMMETS.includes(st.grommet.sku) ? st.grommet.qty : 0;
  if (plateSlots) {
    const plates = CAT.accessories.filter(a => a.group === 'plate');
    const used = plates.reduce((s, a) => s + (st.acc[a.sku] || 0), 0);
    pl.appendChild(el('p', 'field__label plate-head', 'Add connections to the grommet'));

    pl.appendChild(el('p', 'hint plate-lede', `One plate fits in each grommet${plateSlots > 1 ? ` (up to ${plateSlots} in all)` : ''}.`));
    for (const a of plates) {
      const [name, what] = PLATE_TEXT[a.sku] || [noDash(a.label), ''];
      const mx = plateSlots - used + (st.acc[a.sku] || 0);
      const r = el('div', 'acc acc--item', `<span class="acc__text"><b>${name}</b>${price(' · ' + money(a.price) + ' each')}<small>${what}</small></span><span class="acc__ctl"><input type="number" min="0" max="${mx}" value="${st.acc[a.sku] || 0}" aria-label="${name}"></span>`);
      r.querySelector('input').addEventListener('change', e => { st.acc[a.sku] = Math.max(0, Math.min(mx, +e.target.value || 0)); update(); });
      stepper(r.querySelector('input'));
      pl.appendChild(r);
    }
  }
  const al = $('#acc-list'); al.innerHTML = '';

  const baskets = CAT.accessories.filter(a => a.group === 'basket');
  const curB = baskets.find(a => a.sku in st.acc) || baskets[0];
  const L = basketLen(curB.sku), bmax = basketCap(top, row, fam, L);
  const anyFit = baskets.some(a => basketCap(top, row, fam, basketLen(a.sku)) > 0);
  const bl = el('div', 'acc acc--item', `<span class="acc__text"><b>Under-table wire basket</b>${price(' · ' + money(curB.price) + ' each')}<small>Holds power strips and spare cable under the table. Usually one per grommet.</small></span><span class="acc__ctl"><select id="basket-len" aria-label="Basket length"></select><input type="number" min="0" max="${bmax}" value="${st.acc[curB.sku] || 0}" aria-label="Number of wire baskets"></span>`);
  const sel = bl.querySelector('select');
  for (const a of baskets) { const op = el('option', null, basketLen(a.sku) + '" long' + price(' · ' + money(a.price))); op.value = a.sku; if (a === curB) op.selected = true; sel.appendChild(op); }
  sel.addEventListener('change', () => { const q = st.acc[curB.sku] || 0; for (const a of baskets) delete st.acc[a.sku]; st.acc[sel.value] = q; update(); });
  bl.querySelector('input').addEventListener('change', e => {
    const was = st.acc[curB.sku] || 0;
    for (const a of baskets) delete st.acc[a.sku];
    st.acc[sel.value] = Math.max(0, Math.min(bmax, +e.target.value || 0));
    if (!was && st.acc[sel.value]) { xrayPick = null; setXray(true); }
    update();
  });
  al.appendChild(bl);
  if (!bmax) bl.querySelector('input').disabled = true;
  stepper(bl.querySelector('input'));
  al.appendChild(bmax ? el('p', 'hint acc__fit', `Up to ${bmax} fit under this table.`)
    : anyFit ? el('p', 'hint error', `A ${L}" basket does not fit between the bases of this table. Choose a shorter basket.`)
    : el('p', 'hint', 'There is no room for a wire basket under this table.'));

  const spine = CAT.accessories.find(a => a.group === 'spine');
  if (spine) {
    const smax = spineSpots(top, row, fam).length;
    const sr = el('div', 'acc acc--item', `<span class="acc__text"><b>Vertical cable spine</b>${price(' · ' + money(spine.price) + ' each')}<small>Takes cables from the table down to the floor.</small></span><span class="acc__ctl"><input type="number" min="0" max="${smax}" value="${st.acc[spine.sku] || 0}" aria-label="Number of cable spines"${smax ? '' : ' disabled'}></span>`);
    sr.querySelector('input').addEventListener('change', e => {
      const was = st.acc[spine.sku] || 0;
      st.acc[spine.sku] = Math.max(0, Math.min(smax, +e.target.value || 0));
      if (!was && st.acc[spine.sku]) { xrayPick = null; setXray(true); }
      update();
    });
    stepper(sr.querySelector('input'));
    al.appendChild(sr);
    if (!smax) al.appendChild(el('p', 'hint', 'A cable spine stands on the floor under the center of the table, where this base sits, so this table cannot take one.'));
    else if (smax > 1) al.appendChild(el('p', 'hint acc__fit', `Up to ${smax} fit between the bases of this table.`));
  }
  $('#row-grommet-rot').style.display = o && st.grommet.qty ? '' : 'none';
  const rc = $('#grommet-rot-chips'); rc.innerHTML = '';
  for (const [deg, lbl] of [[0, 'Along table'], [90, 'Across table']]) {
    const c = el('button', 'chip' + (st.grommet.rot === deg ? ' active' : ''), lbl); c.addEventListener('click', () => { st.grommet.rot = deg; update(); }); rc.appendChild(c);
  }
}
/** Credenza picker (book p.168-170): the families with a 3-D part, then depth, width, how many and locks. The product
 *  page's panel (compact) shows family and width only: 24" deep, one unit. Priced in the top's edge column. */
function buildCredenzaUI(host, top, compact) {
  const fams = (CAT.credenzas || []).filter(c => c.modelled), cz = st.credenza, cur = fams.find(c => c.code === cz.type);
  const col = CREDENZA_EDGE[st.edge] || 'A1', offered = () => true;   // every book size has a model
  const unitPrice = z => z.prices[col] ?? z.prices.A1;
  const sel = (label, opts, value, onChange) => {
    const f = el('div', 'field', `<label class="field__label">${label}</label><select></select>`); const s = f.querySelector('select');
    for (const [v, text] of opts) { const o = el('option', null, text); o.value = v; o.selected = String(v) === String(value); s.appendChild(o); }
    s.addEventListener('change', () => { onChange(s.value); update(); }); host.appendChild(f); return s;
  };
  const from = c => compact ? '' : price(` · from ${money(Math.min(...c.sizes.filter(offered).map(unitPrice)))}`);
  sel('Credenza', [['', 'None'], ...fams.map(c => [c.code, compact ? c.label.replace(/^(.)/, m => m.toUpperCase())  : `${c.label}, ${c.height}" high${from(c)}`])], cz.type, v => { cz.type = v; });
  if (!cur) { if (!compact) host.appendChild(el('p', 'hint', 'A matching credenza near the table (behind, in front or at an end), in the same laminate and edge as the top, with silver pulls.')); return; }
  if (!compact) sel('Where it stands', Object.entries(credPlaces()), cz.place || 'back', v => { cz.place = v; });
  const sizes = cur.sizes.filter(offered);
  if (!compact) sel('Depth', [...new Set(sizes.map(z => z.depth))].map(d => [d, `${d}" deep`]), cz.depth, v => { cz.depth = +v; });
  sel(compact ? 'Credenza width' : 'Width', sizes.filter(z => z.depth === cz.depth).map(z => [z.width, compact ? `${z.width}"` : `${z.width}" wide` + price(' · ' + money(unitPrice(z)))]), cz.width, v => { cz.width = +v; });
  if (compact) return;
  sel('How many', [[1, 'One'], [2, 'Two, joined side by side']], cz.qty, v => { cz.qty = +v; });
  const lock = CAT.options.find(o => o.sku === 'OP-LOCK-ADD');
  const row = el('label', 'opt', `<input type="checkbox" ${cz.locks ? 'checked' : ''}/> <span>Locks on the doors</span><span class="price">${price(`+${money(lock.price)} per lock`)}</span>`);   // one lock per bay; the total carries the sum
  row.querySelector('input').addEventListener('change', e => { cz.locks = e.target.checked; update(); }); host.appendChild(row);
  host.appendChild(el('p', 'hint', `${cur.code === 'BCRZ' && cz.width < 48 ? '2 doors + 2 box drawers, 1 bay' : cur.detail}. Stands ${CREDENZA_CLEAR}" ${credPlaceText(cz.place || 'back')}, doors toward it, in the top's finish and edge${cz.qty > 1 ? '; the two units are finished flush where they meet' : ''}.`));
}
// ---------------------------------------------------------------- Credenza step (6): its own step, like Base
// The step button and pane are made here rather than in the page markup, so the store's copy of the page needs no change.
const CRED_IMG = typeof assetUrl === 'function' ? assetUrl : (x => x);
const credFinish = () => (st.credenza?.finish ? fin(st.credenza.finish) : st.finish);   // its own laminate, or the top's
function installCredenzaStep() {
  if ($('#g-credenza')) return;
  const q = $('.step[data-tab="quote"]');
  const b = el('button', 'step', '<span class="step__num"></span><span class="step__text"><span class="step__name">Credenza</span><span class="step__val" data-val="credenza"></span></span>');
  b.type = 'button'; b.dataset.tab = 'credenza'; b.addEventListener('click', () => showTab('credenza')); q.before(b);
  const pane = el('section', 'pane', '<p class="pane__eyebrow"></p><h2 class="pane__title">Credenza</h2><p class="pane__lede">A matching credenza near the table: along a side or past an end. Pick one to see it; it comes in the top\'s laminate unless you choose another.</p><div class="base-grid" id="cred-cards"></div><div id="cred-detail"></div>');
  pane.id = 'g-credenza'; pane.dataset.pane = 'credenza'; pane.hidden = true; $('#g-quote').before(pane);
  renumberSteps();
}
/** Step numbers and "Step i of N" eyebrows follow TABS (the Start step is the star). */
function renumberSteps() {
  const numbered = TABS.filter(t => t !== 'typicals' && t !== 'simple');
  document.querySelectorAll('.step').forEach(s => { const i = numbered.indexOf(s.dataset.tab), n = s.querySelector('.step__num'); if (i >= 0 && n && !n.classList.contains('step__num--star')) n.textContent = i + 1; s.hidden = !TABS.includes(s.dataset.tab); });
  document.querySelectorAll('.pane').forEach(p => { const i = TABS.indexOf(p.dataset.pane), e = p.querySelector('.pane__eyebrow'); if (e && (TABS[0] === 'simple' ? i >= 0 : i >= 1)) e.textContent = `Step ${TABS[0] === 'simple' ? i + 1 : i} of ${TABS.length - (TABS[0] === 'simple' ? 0 : 1)}`; });   // the Start pane keeps its own eyebrow
}
function buildCredenzaStep(top) {
  const grid = $('#cred-cards'), det = $('#cred-detail'); if (!grid) return;
  const fams = (CAT.credenzas || []).filter(c => c.modelled), cz = st.credenza, cur = fams.find(c => c.code === cz.type);
  const col = CREDENZA_EDGE[st.edge] || 'A1', offered = () => true, unitPrice = z => z.prices[col] ?? z.prices.A1;
  grid.innerHTML = '';
  const none = el('button', 'base-card base-card--none' + (!cz.type ? ' active' : ''), '<span class="thumb"><span>No credenza</span></span><span class="name">None</span>');
  none.type = 'button'; none.addEventListener('click', () => { cz.type = ''; update(); }); grid.appendChild(none);
  for (const c of fams) {
    const from = Math.min(...c.sizes.filter(offered).map(unitPrice));
    const card = el('button', 'base-card' + (c.code === cz.type ? ' active' : ''), `<span class="thumb"><img src="${CRED_IMG(`assets/credenza-thumbs/${c.code}.png${relQ}`)}" alt="" loading="lazy"></span><span class="name">${c.label}<small>${c.detail}, ${c.height}" high${price(' · from ' + money(from))}</small></span>`);
    card.type = 'button'; card.querySelector('img').addEventListener('error', e => (e.target.style.visibility = 'hidden'));
    card.addEventListener('click', () => { const was = cz.type; cz.type = c.code; update().then(() => { if (!was) frameTable(false, credView()); }); }); grid.appendChild(card);
  }
  det.innerHTML = '';
  if (!cur) { det.appendChild(el('p', 'hint', 'Credenzas from the same price book as the table: standard (29") or buffet (36") height, plain, with drawers, or media (a wire cut-out in the back); 36" to 78" wide, 19" or 24" deep, with silver pulls. Pick where it stands: along a side or past an end.')); return; }
  const sel = (label, opts, value, onChange) => {
    const f = el('div', 'field', `<label class="field__label">${label}</label><select></select>`); const s = f.querySelector('select');
    for (const [v, text] of opts) { const o = el('option', null, text); o.value = v; o.selected = String(v) === String(value); s.appendChild(o); }
    s.addEventListener('change', () => { onChange(s.value); update(); }); det.appendChild(f);
  };
  {   // where it stands: chips, like the shape picker
    const f = el('div', 'field', '<span class="field__label">Where it stands</span><div class="chips cred-place"></div>'), box = f.querySelector('.chips');
    for (const [k, label] of Object.entries(credPlaces())) {
      const c = el('button', 'chip' + ((cz.place || 'back') === k ? ' active' : ''), label); c.type = 'button';
      c.addEventListener('click', () => { cz.place = k; update().then(() => frameTable(false, credView())); }); box.appendChild(c);
    }
    det.appendChild(f);
  }
  const sizes = cur.sizes.filter(offered);
  sel('Width', sizes.filter(z => z.depth === cz.depth).map(z => [z.width, `${z.width}" wide${price(' · ' + money(unitPrice(z)))}`]), cz.width, v => { cz.width = +v; });
  sel('Depth', [...new Set(sizes.map(z => z.depth))].map(d => [d, `${d}" deep`]), cz.depth, v => { cz.depth = +v; });
  sel('How many', [[1, 'One'], [2, 'Two, joined side by side']], cz.qty, v => { cz.qty = +v; });
  const lock = CAT.options.find(o => o.sku === 'OP-LOCK-ADD');
  const row = el('label', 'opt', `<input type="checkbox" ${cz.locks ? 'checked' : ''}/> <span>Locks on the doors</span><span class="price">${price(`+${money(lock.price)} per lock`)}</span>`);   // one lock per bay; the total carries the sum
  row.querySelector('input').addEventListener('change', e => { cz.locks = e.target.checked; update(); }); det.appendChild(row);
  // finish: the top's laminate until another is picked (as the base finish works on the Finishes step)
  const linked = !cz.finish, f = el('div', 'field fin-cred', `<span class="field__label">Finish ${linked ? '<span class="fin-note">matches the table top</span>' : '<button class="linkbtn" type="button" id="cred-match">Match the table top</button>'}</span><div class="swatches"></div>`);
  det.appendChild(f);
  swatches(f, credFinish(), x => { cz.finish = x.name === st.finish?.name ? null : x.name; update(); }, x => (x.premium ? price('+10%') : ''), 'TFL');
  $('#cred-match')?.addEventListener('click', () => { cz.finish = null; update(); });
  det.appendChild(el('p', 'hint', `${cur.code === 'BCRZ' && cz.width < 48 ? '2 doors + 2 box drawers, 1 bay' : cur.detail}. Stands ${CREDENZA_CLEAR}" ${credPlaceText(cz.place || 'back')}, doors toward it, with the same edge as the top${cz.qty > 1 ? '; the two units are finished flush where they meet' : ''}.`));
}
/** Frame the credenza(s) alone: on the Credenza step, and for its thumbnails. */
function frameCredenza(keepAngle = true) {
  if (!credRef) return frameTable(keepAngle);
  const bb = new THREE.Box3().setFromObject(credRef), ctr = bb.getCenter(new THREE.Vector3());
  controls.target.copy(ctr);
  const dir = keepAngle && camera.position.length() > 0 ? camera.position.clone().sub(controls.target).normalize() : new THREE.Vector3(0.6, 0.45, 0.8).normalize();
  camera.position.copy(ctr).add(dir.multiplyScalar(fitDistance(bb, dir, 1.25))); controls.update();
}
/** Product page (?pdp=): one short panel with the main choices (size, height, top, base, power). Construction stays as
 *  the product is made; everything else is one link away in the full builder. */
function buildSimpleUI(top) {
  let pane = $('#g-simple');
  if (!pane) {
    pane = el('section', 'pane pane--simple', '<p class="pane__eyebrow">Customize</p><h2>Your table</h2><div id="simple-body"></div>');
    pane.id = 'g-simple'; pane.dataset.pane = 'simple'; pane.hidden = tab !== 'simple';
    $('#g-typicals').parentNode.insertBefore(pane, $('#g-typicals'));
  }
  const body = $('#simple-body'); body.innerHTML = '';
  const field = (label, extra = '', host = body) => { const f = el('div', 'field', `<span class="field__label">${label}${extra}</span><div class="chips"></div>`); host.appendChild(f); return f.querySelector('.chips'); };
  const chip = (box, html, on, pick, dis) => { const c = el('button', 'chip' + (on ? ' active' : ''), html); c.type = 'button'; c.disabled = !!dis; c.addEventListener('click', pick); box.appendChild(c); return c; };
  const fam = CAT.families[st.famCode], round = top.shape === 'round' || top.shape === 'square';
  // size
  const sizes = [...new Map(topsForShape().map(t => [sizeKeyOf(t), t])).values()].sort((a, b) => a.depth * a.length - b.depth * b.length);
  const row = el('div', 'simple-row'); body.appendChild(row);   // size and height side by side
  const sf = el('div', 'field', '<label class="field__label" for="simple-size">Size</label><select id="simple-size"></select>'); row.appendChild(sf);
  const sel = sf.querySelector('select');
  for (const t of sizes) { const o = el('option', null, (round ? `${t.depth}" ${t.shape}` : `${t.depth}" × ${t.length}"`)); o.value = sizeKeyOf(t); o.selected = o.value === st.sizeKey; sel.appendChild(o); }
  sel.addEventListener('change', () => { st.sizeKey = sel.value; update(); });
  // height, where the base comes in both
  const rows = compatibleBases(CAT, top, st.famCode);
  if (rows.some(r => r.height > 35) && rows.some(r => r.height && r.height <= 35)) {
    const hf = el('div', 'field', '<label class="field__label" for="simple-height">Height</label><select id="simple-height"></select>'); row.appendChild(hf);
    const hs = hf.querySelector('select');
    for (const [k, l] of [['seated', 'Seated'], ['standing', 'Standing']]) { const o = el('option', null, l); o.title = k === 'seated' ? '29" table height' : '42" table height'; o.value = k; o.selected = st.height === k; hs.appendChild(o); }
    hs.addEventListener('change', () => { st.height = hs.value; st.baseSku = null; update(); });
  }
  // top finish (and the middle section of a two-tone top)
  const lam = st.laminate === 'hpl' ? 'HPL' : 'TFL', cap = f => (f.premium ? price('+10%') : '');
  // small round swatches here (the full builder's picture cards take most of the panel): the name is on hover and
  // next to the heading for the one picked
  const sw = (title, active, pick, l = lam) => {
    const now = active ? `: <b class="fin-simple__now">${active.name}</b>${active.premium ? ' <small class="fin-simple__prem">premium</small>' : ''}` : '';
    const h = el('div', 'field fin-simple', `<span class="field__label">${title}${now}</span><div class="swatches"></div>`); body.appendChild(h); swatches(h, active, pick, cap, l);
    h.querySelectorAll('.swatch').forEach(b => { b.title = b.querySelector('.name').firstChild.textContent + (b.classList.contains('premium') ? ' (premium, +10%)' : ''); b.setAttribute('aria-label', b.title); });
    return h;
  };
  sw(top.multiFinish ? 'Top: ends' : 'Top finish', st.finish, f => { st.finish = f; update(); });
  if (top.multiFinish) sw('Top: middle', st.midFinish, f => { st.midFinish = f; update(); });
  // base: metal colour, or a laminate that follows the top until another is picked
  if (fam.metal) {
    const mb = field(fam.wirebox ? 'Leg color' : 'Base color');
    for (const code of fam.colors ? Object.keys(fam.colors) : ['SV', 'BK', 'WT'])
      chip(mb, `<span class="dot" style="background:${METALS[code]?.hex || '#999'}"></span>${((fam.colors || {})[code] || METALS[code]?.name || code).replace('*', '')}`, st.metalCode === code, () => { st.metalCode = code; update(); }).classList.add('chip--dot');
  }
  if (!fam.metal || fam.wirebox) {
    const h = sw(fam.wirebox ? 'Pedestal finish' : 'Base finish', st.baseFinish, f => { st.baseFinish = f; st.baseLinked = f.name === st.finish?.name; update(); }, HPL_ONLY_BASES.includes(fam.family) ? 'HPL' : lam);
    h.querySelector('.field__label').innerHTML += st.baseLinked ? ' <span class="fin-note">matches the top</span>' : ' <button class="linkbtn" type="button">Match the top</button>';
    h.querySelector('.linkbtn')?.addEventListener('click', () => { st.baseLinked = true; update(); });
  }
  // power: how many of the product's own grommet (or the first one offered), up to what this top and base take
  const gmax = gmaxNow(top), gsku = st.grommet.sku || TYP.typicals.find(x => x.code === PDP)?.cfg.grommet?.sku || Object.keys(CAT.grommetSpecs)[0];
  if (gmax > 0) {
    const pb = field('Power & data');
    for (let q = 0; q <= gmax; q++) chip(pb, q ? `${q} power grommet${q > 1 ? 's' : ''}` : 'None', (st.grommet.sku ? st.grommet.qty : 0) === q, () => { st.grommet.sku = q ? gsku : ''; st.grommet.qty = q; update(); });
  }
  // credenza: family and width, side by side (24" deep, one unit; depth, a second unit and locks are in the full builder)
  const crow = el('div', 'simple-row simple-row--wide'); body.appendChild(crow); buildCredenzaUI(crow, top, true);
  const full = new URL(cfgURL()); full.searchParams.delete('pdp');
  body.appendChild(el('p', 'hint simple-more', `Edges, thickness, laminate, power models and wire management: <a href="${full}" target="_blank" rel="noopener">open the full table builder</a>.`));
}
/** Under a popular table's name: its size, about how many it seats, and the listed price (phones show these; the desktop
 *  grid keeps the name alone). */
function typMeta(t) {
  const top = CAT.tops.find(x => x.shape === t.cfg.shape && sizeKeyOf(x) === t.cfg.sizeKey), n = seatEstimate(top);
  const bits = [top ? sizeLabel(top) : '', n ? `seats about ${n}` : ''].filter(Boolean).join(' · ');
  return `<small>${bits}</small>${PRESET_PRICE[t.code] ? `<b>${money(PRESET_PRICE[t.code])}</b>` : ''}`;
}
function buildTypicalsUI() {
  if (!TYP) return;
  const grid = $('#typ-grid'); grid.innerHTML = '';

  const scratch = el('button', 'typ-card typ-card--scratch' + (!st.typical ? ' active' : ''), '<span class="plus">+</span><span class="name">Start from scratch<small>Build your own, step by step</small></span>');
  scratch.addEventListener('click', () => { const wg = st.whiteGlove; for (const k of Object.keys(st)) delete st[k]; Object.assign(st, JSON.parse(ST_DEFAULT), { whiteGlove: wg }); scratchLook(); typSnap = null; anchor = null; finPart = 'top'; telemetry.track('start_from_scratch'); cfgSilent = true; update(); showTab('shape'); });
  grid.appendChild(scratch);
  for (const t of TYP.typicals) {
    const c = el('button', 'typ-card' + (st.typical === t.code ? ' active' : ''), `<img src="${assetUrl(t.image)}" alt="" loading="lazy"><span class="name">${t.cfg.name}<span class="typ-meta">${typMeta(t)}</span></span>`);
    c.addEventListener('click', () => loadTypical(t));
    grid.appendChild(c);
  }
  const info = $('#typ-info'); const t = TYP.typicals.find(x => x.code === st.typical);
  info.hidden = !t;
  if (t) {

    const li = (l, cls = '') => `<li class="${cls}">×${l.qty} ${noDash(l.desc).replace(/: 10% of \$[\d,]+$/, ' (10%)')}</li>`;
    const items = groupParts(anchor?.code === t.code ? anchor.lines : []).map(g => `<li class="layer"><span class="layer-name">${g.label.replace(/^\d+ · /, '')}</span><ul>${g.lines.map(h => li(h) + (h.children.length ? `<ul class="mods">${h.children.map(m => li(m, 'mod')).join('')}</ul>` : '')).join('')}</ul></li>`).join('');

    const oos = t.bom.filter(l => l.outOfScope).map(l => l.sku);
    const room = [oos.some(s => /^CF-(STDB|BCRZ|BUF|MCRZ)/.test(s)) && 'a credenza', oos.some(s => /^CF-MDCB/.test(s)) && 'a presentation board'].filter(Boolean);
    info.innerHTML = `<b>${t.cfg.name}</b><ul class="layers">${items}</ul>${room.length && !OWN_PICTURES ? `<p class="hint">The photo also shows ${room.join(' and ')}, which this preset does not include.</p>` : ''}<p class="hint">Loaded into the builder. Use the steps above to change anything.${t.cfg.credenza ? ' It comes with its credenza: change or remove it in the Credenza step.' : ''}</p>`;
  }
}

function scratchLook() { st.finish = fin('California Walnut'); st.baseFinish = st.finish; st.sideFinish = fin('Glacier White'); }

let anchor = null;
function presetAnchor(t) {
  if (!PRESET_PRICE[t.code]) return null;
  const keep = { ...st, grommet: { ...st.grommet }, acc: { ...st.acc }, baseOpts: { ...st.baseOpts } };
  applyPreset(t);
  const top = settle(), bom = quoteBOM(top), snap = core();
  const power = PAGE_WITHOUT_POWER.has(t.code) ? groupParts(bom.lines).find(g => g.key === 'power')?.total || 0 : 0;
  for (const k of Object.keys(st)) delete st[k]; Object.assign(st, keep);
  return { code: t.code, adj: (PRESET_TOTAL[t.code] ?? x99(PRESET_PRICE[t.code] + power)) - bom.total, snap, keys: new Set(bom.lines.map(l => partKey(l.sku))), lines: bom.lines };
}
function loadTypical(t) {
  applyPreset(t);
  anchor = presetAnchor(t);
  finPart = 'top';
  pendingSnap = true;
  telemetry.track('preset_loaded', { code: t.code, name: t.cfg.name }); cfgSilent = true;
  update();
}

const untouched = () => !!(anchor && st.typical === anchor.code && core() === anchor.snap);

const sameTable = () => { const s = JSON.parse(anchor.snap); return st.shape === s.shape && st.sizeKey === s.sizeKey && st.famCode === s.famCode; };
function applyPreset(t) {
  const c = t.cfg, photo = PHOTO_FINISH[t.code];
  Object.assign(st, {
    typical: t.code, shape: c.shape, sizeKey: c.sizeKey, famCode: c.famCode, baseSku: null,
    finish: fin(photo || c.finish || 'Carmelo Mist'), midFinish: fin(c.midFinish || 'Glacier White'), baseFinish: fin(photo || c.baseFinish || c.finish || 'Carmelo Mist'), sideFinish: null, sideMatch: false,
    metalCode: c.metalCode || 'SV', kick: c.kick || 'BK', reveal: !!c.reveal, rounded: false,
    grommet: Object.assign({ sku: '', color: 'BK', qty: 0, rot: 0 }, c.grommet || {}), acc: {}, baseOpts: {},
    multi: !!c.midFinish, pieces: c.midFinish ? 3 : (c.pieces || 1), height: 'seated',
    credenza: Object.assign({ type: '', depth: 24, width: 72, qty: 1, locks: false, place: 'back' }, c.credenza || {}),
    baseLinked: !!photo || !c.baseFinish || c.baseFinish === (c.finish || 'Carmelo Mist'),
  });
  for (const l of t.bom) if (CAT.accessories.some(a => a.sku === l.sku)) st.acc[l.sku] = l.qty;
  const topLine = t.bom.find(l => /^CF-(REC|RTR|BOT|BOW|SLN|CT)/.test(l.sku)), m = topLine && topLine.sku.match(/-(T|H)\d-([A-Z]\d)$/);
  if (m) { st.laminate = m[1] === 'H' ? 'hpl' : 'tfl'; if (CAT.edges[m[2]]) st.edge = m[2]; }
  st.revealColor = 'BK';
  st.doors = t.bom.find(l => l.sku === 'OP-CONF-57')?.qty || 0;
  if (CAT.families[c.famCode].family === 'malibu') {
    const svp = t.bom.some(l => /^OP-CONF-S(L)?VP/.test(l.sku));
    st.baseOpts['OP-CONF-SVP'] = svp; if (!svp) st.sideFinish = fin('Glacier White');
  }
  Object.assign(st, PRESET_FIX[t.code] || {});
}

function quoteBOM(top) {
  const row = compatibleBases(CAT, top, st.famCode).find(r => r.sku === st.baseSku) || null;
  const bom = buildBOM(CAT, { top, cfg: { edge: st.edge, laminate: st.laminate, finish: st.finish, midFinish: st.midFinish, rounded: st.rounded, reveal: st.reveal },
    baseRow: row, famCode: st.famCode, baseFinish: st.baseFinish, sideFinish: st.sideFinish, baseOpts: st.baseOpts, metalCode: st.metalCode, grommet: st.grommet, acc: st.acc, doors: st.doors, credenza: { ...st.credenza, finishObj: credFinish() },
    notches: notchCount(top, row, CAT.families[st.famCode]) });
  for (const l of bom.lines) {
    if (PLATE_TEXT[l.sku]) l.desc = `${PLATE_TEXT[l.sku][0]}, a connection plate for the power grommet`;
    if (isPowerSku(l.sku)) { const n = CAT.families[st.famCode].name; l.desc = l.desc.replace(n, `${n} (power model)`); }
  }
  return bom;
}

const partKey = sku => String(sku).split('-').slice(0, 2).join('-');
function spreadOver(lines, adj, keys) {

  const own = l => l.price != null && !l.na && (!keys || keys.has(partKey(l.sku)));
  let base = lines.filter(own).reduce((s, l) => s + l.price, 0);
  if (!adj) return lines;
  if (!base) { keys = null; base = lines.filter(own).reduce((s, l) => s + l.price, 0); }
  if (!base) return lines;
  const k = (base + adj) / base;
  const out = lines.map(l => {
    if (!own(l)) return l;
    const q = l.qty || 1;
    return { ...l, price: Math.round(l.price / q * k) * q, desc: l.desc.replace(/: 10% of \$[\d,]+$/, ' (10%)') };
  });
  const top = out.find(l => own(l) && (l.qty || 1) === 1);
  if (top) top.price += lines.reduce((s, l) => s + (l.price || 0), 0) + adj - out.reduce((s, l) => s + (l.price || 0), 0);
  return out;
}

const shipRO = new ResizeObserver(es => { for (const e of es) $('#bom').style.setProperty(e.target.classList.contains('glove') ? '--glove-h' : '--ship-h', e.target.offsetHeight + 'px'); });

const WHITE_GLOVE = 199;
function buildQuote(top) {
  const bom = quoteBOM(top);

  const adj = anchor && st.typical === anchor.code && sameTable() ? anchor.adj : 0, sub = bom.total + adj, free = untouched();

  const qty = Math.max(1, Math.min(50, Math.round(st.qty || 1)));
  const glove = st.whiteGlove ? WHITE_GLOVE : 0, total = sub * qty + glove;

  const t = $('#bom'); t.innerHTML = '';
  const spread = spreadOver(bom.lines, adj, anchor?.keys), groups = groupParts(spread);
  lastLines = spread; lastSub = sub; lastQty = qty;
  const fam = CAT.families[st.famCode];
  const dec = n => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const round = top.shape === 'round' || top.shape === 'square';
  const dims = (round ? `${top.depth}"` : `${top.depthMin !== top.depth ? top.depthMin + '-' : ''}${top.depth}"D × ${top.length}"L`) + ` (${ftDims(top)})`;
  const head = round ? `${dims} ${top.shapeLabel} conference table` : `${dims} · ${top.pieces}-piece ${top.shapeLabel} conference table`;
  $('#quote-title').textContent = `${dims} ${top.shapeLabel} conference table on ${fam.name}`;
  t.appendChild(el('thead', null, `<tr><th class="sku">Item</th><th class="desc">Description</th><th class="qty">Qty</th>${DEV ? '<th class="unit">Price</th><th class="total">Total</th>' : ''}</tr>`));
  const body = el('tbody'); t.appendChild(body);

  const wide = (desc, total = '') => `<td class="pad"></td><td class="desc">${desc}</td>` + (DEV ? `<td class="pad"></td><td class="pad unit"></td><td class="num total">${total}</td>` : '<td class="pad"></td>');
  body.appendChild(el('tr', 'head', wide(head)));

  const listed = free; t.classList.toggle('listed', listed);
  const rowFor = (l, cls) => {
    const unit = listed ? '' : l.na ? 'N/A' : dec(l.price / (l.qty || 1)), tot = listed ? '' : l.na ? 'N/A' : dec(l.price);

    const code = /^[TH]1 → [TH]2$/.test(l.sku) ? '' : l.sku;
    const r = el('tr', 'line' + (cls ? ' ' + cls : ''), `<td class="sku">${code}</td><td class="desc">${noDash(l.desc)}</td><td class="qty">${l.qty}</td>${DEV ? `<td class="num unit">${unit}</td><td class="num total">${tot}</td>` : ''}`);
    r.querySelector('.sku').dataset.meta = `Qty ${l.qty}${DEV && l.qty > 1 && unit ? ` × ${unit}` : ''}`;
    return r;
  };
  for (const g of groups) {
    body.appendChild(el('tr', 'group', wide(g.label.replace(/^\d+ · /, ''))));
    for (const h of g.lines) { body.appendChild(rowFor(h)); for (const m of h.children) body.appendChild(rowFor(m, 'mod')); }
  }
  if (DEV) body.appendChild(el('tr', 'subtotal', wide(qty > 1 ? 'PRICE PER TABLE' : listed ? 'PRICE' : 'SUBTOTAL', dec(sub))));
  shipRO.disconnect(); t.style.setProperty('--glove-h', '0px');

  if (glove) shipRO.observe(body.appendChild(el('tr', 'glove', DEV ? wide('WHITE GLOVE DELIVERY', dec(glove)) : wide('White Glove Delivery: added'))));

  const shipTxt = free ? 'INCLUDED' : 'NOT INCLUDED';
  shipRO.observe(body.appendChild(el('tr', 'ship', DEV ? wide('SHIPPING (CURBSIDE FREIGHT, CONTINENTAL US)', shipTxt) : wide(`Shipping (curbside freight, continental US): ${shipTxt.toLowerCase()}`))));

  $('#quote-terms').innerHTML = '<strong>Made to order. Ships in 2 to 3 weeks</strong> (tables with metal legs can take around 4 weeks). '
    + (free ? 'Shipping by curbside freight within the continental US is included.' : 'Shipping is not included; contact us for a freight quote to your address.');
  $('#total').textContent = total.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  lastTotal = total;

  $('#pb-total').textContent = money(total);
  const mp = $('#mhead-price'); if (mp) mp.textContent = money(total);
  const shown = ['top', 'base', 'power', 'wire', 'room'];

  $('#pb-lines').innerHTML = (listed ? '' : shown.map(k => { const g = groups.find(x => x.key === k); const v = (g ? g.total : 0) * qty; const label = k === 'power' ? 'Power & data' : k === 'wire' ? 'Wire management' : k === 'room' ? 'Credenza' : k[0].toUpperCase() + k.slice(1); return `<div class="${v ? '' : 'zero'}"><span>${label}</span><span>${money(v)}</span></div>`; }).join(''))
    + (qty > 1 ? `<div class="pb-qty"><span>Tables</span><span>× ${qty}${listed ? ' · ' + money(sub) + ' each' : ''}</span></div>` : '')
    + (glove ? `<div class="pb-glove"><span>White Glove Delivery</span><span>${money(glove)}</span></div>` : '')

    + (free ? '<div class="pb-ship"><span>Shipping</span><span>Included</span></div>' : '');
  $('#pricebox').hidden = !DEV;
  const qs = $('#quote-summary');
  if (qs) qs.innerHTML = (listed ? `<div><span>${head}</span><span>${money(sub)}</span></div>` : $('#pb-lines').innerHTML.replace(/<div class="zero">.*?<\/div>/g, ''))
    + `<div class="qs__total"><span>Total</span><span>${money(total)}</span></div>`;
  $('#glove').checked = !!st.whiteGlove; $('#glove-price').textContent = price('+' + money(WHITE_GLOVE));
  $('#qty').value = qty; stepper($('#qty'));
  $('#qty-each').textContent = qty > 1 && DEV ? `${money(sub)} per table` : '';
  paintBuy();
}

const CHECKOUT = false;
const SALES_EMAIL = 'sales@officesimpli.com';
const HOOK_URL = document.body.dataset.hookUrl || 'https://officesimpli.app.n8n.cloud/webhook/os-builder';

const FORCE_LIVE = window.CTB_FORCE_LIVE_VISIT === true;
const LIVE = FORCE_LIVE || /(^|\.)officesimpli\.com$|\.myshopify\.com$|\.shopifypreview\.com$/.test(location.hostname);

const invoiceOnly = () => CHECKOUT && untouched() && DEV;
const buildSummary = () => ({ title: $('#quote-title').textContent, qty: lastQty, total: money(lastTotal), custom: customNote(currentTop()),
  shipping: (untouched() ? 'shipping included' : 'shipping quoted to your address') + (st.whiteGlove ? ', with White Glove Delivery' : ''), link: cfgURL().toString() });

const mailtoFor = (b, d) => {
  const L = { business: 'Business', home: 'Home', yes: 'Yes', no: 'No', 'not sure': 'Not sure', card: 'Card', invoice: 'Invoice or purchase order' };
  const answers = d
    ? `\nName: ${d.name || ''}\nEmail: ${d.email || ''}\nPhone: ${d.phone || ''}\nCompany: ${d.company || ''}\nDelivery ZIP code: ${d.zip || ''}\nBusiness or home address: ${L[d.address] || ''}\nLoading dock: ${L[d.dock] || d.dock || ''}\nAssembly quote: ${L[d.assembly] || ''}\nPaying by: ${L[d.pay] || ''}\n${d.notes ? '\n' + String(d.notes).slice(0, 300) + '\n' : ''}`
    : '\nDelivery ZIP code:\nBusiness or home address:\n';
  return `mailto:${SALES_EMAIL}?subject=${encodeURIComponent('Conference table price request: ' + b.title)}&body=${encodeURIComponent(`${b.qty > 1 ? b.qty + ' tables: ' : ''}${b.title}\n${b.custom ? b.custom + '\n' : ''}${b.total}, ${b.shipping}\n${b.link}\n${answers}`)}`;
};
$('#glove').addEventListener('change', e => { st.whiteGlove = e.target.checked; update(); });
$('#qty').addEventListener('change', e => { st.qty = Math.max(1, Math.min(50, Math.round(+e.target.value || 1))); update(); });
function paintBuy() {
  $('#buy-note').innerHTML = invoiceOnly()
    ? '<b>Buy it now</b>: pay by card at our secure checkout, shipping included. <b>Get my delivered price</b>: we email you the price and an invoice.'
    : '<b>Get my delivered price</b>: tell us where it is going and we will email you the price delivered to your door. Pay by card, or by invoice or purchase order.';
  $('#rq-mail').href = mailtoFor(buildSummary());
}
const reveal = node => node.scrollIntoView({ block: 'start', behavior: 'smooth' });
const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const VID = (() => { try { const k = 'os_builder_vid'; let v = localStorage.getItem(k); if (!v) { v = Math.random().toString(36).slice(2, 10) + Date.now().toString(36); localStorage.setItem(k, v); } return v; } catch (e) { return 'na'; } })();

const TRACK = (() => {
  const o = {};
  for (const k of ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) if (QS.get(k)) o[k] = QS.get(k).slice(0, 200);
  try {
    const K = 'os_builder_track';
    if (Object.keys(o).length) localStorage.setItem(K, JSON.stringify({ t: Date.now(), o }));
    else { const s = JSON.parse(localStorage.getItem(K) || 'null'); if (s && s.o && Date.now() - s.t < 90 * 864e5) Object.assign(o, s.o); }
  } catch (e) {}
  return o;
})();
const SRC = (QS.get('src') || '').slice(0, 40).replace(/[^a-z0-9_-]/gi, '');
const SEND_TIMEOUT_MS = window.CTB_SEND_TIMEOUT_MS || 15000;
function send(payload, wait) {
  const body = JSON.stringify({ ...payload, vid: VID, ts: new Date().toISOString(), page: location.pathname, preview: /preview_theme_id/.test(location.search), ref: document.referrer || '', ...TRACK, src: SRC });

  const ctl = wait && typeof AbortController === 'function' ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), SEND_TIMEOUT_MS) : 0;
  const req = fetch(HOOK_URL, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body, keepalive: !wait, mode: 'cors', credentials: 'omit', signal: ctl ? ctl.signal : undefined });
  return wait ? req.finally(() => clearTimeout(timer)) : req.catch(() => {});
}

function isTestVisit() {
  try {
    if (FORCE_LIVE) return false;
    if (navigator.webdriver) return true;
    if (window.Shopify && Shopify.theme && Shopify.theme.role && Shopify.theme.role !== 'main') return true;
    const m = /[?&]os_test=([01])\b/.exec(location.search);
    if (m) { if (m[1] === '1') localStorage.setItem('os_calc_test', '1'); else localStorage.removeItem('os_calc_test'); }
    return localStorage.getItem('os_calc_test') === '1';
  } catch (e) { return false; }
}

const EVQ_KEY = 'os_builder_evq';
const EVQ = (() => { try { const s = JSON.parse(localStorage.getItem(EVQ_KEY) || '[]'); localStorage.removeItem(EVQ_KEY); return Array.isArray(s) ? s.slice(-40) : []; } catch (e) { return []; } })();
const keepEvents = events => { try { const s = JSON.parse(localStorage.getItem(EVQ_KEY) || '[]'); localStorage.setItem(EVQ_KEY, JSON.stringify((Array.isArray(s) ? s : []).concat(events).slice(-40))); } catch (e) {} };
function logEvent(event, extra = {}) {
  if (!LIVE || QS.has('view') || QS.has('thumb') || isTestVisit()) return;
  EVQ.push({ event, ...extra, ts: new Date().toISOString() });
  if (EVQ.length >= 12) flushEvents();
}

function flushEvents(leaving) {
  try { const s = JSON.parse(localStorage.getItem(EVQ_KEY) || '[]'); if (Array.isArray(s) && s.length) { localStorage.removeItem(EVQ_KEY); EVQ.unshift(...s.slice(-40)); } } catch (e) {}
  if (!EVQ.length) return;
  const events = EVQ.splice(0, EVQ.length);
  const body = JSON.stringify({ event: 'batch', events, vid: VID, ts: new Date().toISOString(), page: location.pathname, preview: /preview_theme_id/.test(location.search), ref: document.referrer || '', ...TRACK, src: SRC });
  if (leaving === true) {
    try { if (navigator.sendBeacon && navigator.sendBeacon(HOOK_URL, new Blob([body], { type: 'text/plain' }))) return; } catch (e) {}
  }
  try {
    fetch(HOOK_URL, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body, keepalive: true, mode: 'cors', credentials: 'omit' })
      .then(r => { if (!r.ok && r.status !== 400) keepEvents(events); }, () => keepEvents(events));
  } catch (e) { keepEvents(events); }
}
addEventListener('pagehide', () => flushEvents(true));
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushEvents(true); });

const RQ_ERROR_DEFAULT = $('#rq-error')?.innerHTML || '';
let requestId = '';

let formKind = null, rqSent = false;
function openRequest(assembly) {
  const f = $('#rq-form'), b = buildSummary();
  formKind = assembly ? 'assembly' : untouched() ? 'invoice' : 'delivered'; rqSent = false; telemetry.intent('sales_form_open', { kind: formKind });
  $('#rq-done').hidden = true; f.hidden = false;
  const er = $('#rq-error'); er.hidden = true; er.innerHTML = RQ_ERROR_DEFAULT; $('#rq-send').textContent = 'Send';
  requestId = VID + '-' + Date.now().toString(36);
  if (assembly) f.elements.assembly.value = 'yes';
  $('#rq-sum').textContent = `${b.qty > 1 ? b.qty + ' tables: ' : ''}${b.title}. ${b.total}${untouched() ? ', shipping included' : ' before shipping'}${st.whiteGlove ? ', White Glove Delivery added' : ''}.`;
  $('#rq-dialog').showModal(); f.elements.name.focus();
  logEvent('request_open', { total: Math.round(lastTotal), qty: lastQty, preset: st.typical || null });
}
const closeRequest = () => $('#rq-dialog').close();
const cancelRequest = () => { if (!rqSent) telemetry.track('sales_form_cancel', { kind: formKind }); closeRequest(); };
$('#rq-close').addEventListener('click', cancelRequest);
$('#rq-cancel').addEventListener('click', cancelRequest);
$('#assembly-ask').addEventListener('click', () => openRequest(true));
function checkout() { openRequest(false); }
$('#rq-form').addEventListener('submit', async e => {
  e.preventDefault();
  const f = e.currentTarget, ok = f.checkValidity(); const er0 = $('#rq-error'); if (!ok) er0.innerHTML = RQ_ERROR_DEFAULT; er0.hidden = ok;
  if (!ok) { f.querySelector('input:invalid, textarea:invalid')?.focus(); telemetry.track('sales_form_invalid', { kind: formKind }); return; }
  const d = Object.fromEntries(new FormData(f)), b = buildSummary(), btn = $('#rq-send');
  const lead = { event: 'lead', test: isTestVisit(), request_id: requestId, notch_risk: lastNotchRisk, custom_length: customForSales(currentTop()), ...d, qty: b.qty, title: b.title, total: Math.round(lastTotal), per_table: Math.round(lastSub),
    white_glove: !!st.whiteGlove, shipping: untouched() ? 'included' : 'to quote', preset: st.typical || null, untouched: untouched(), link: b.link,
    preset_name: TYP?.typicals.find(x => x.code === st.typical)?.cfg.name || null,
    parts: lastLines.map(l => ({ sku: /^[TH]1 → [TH]2$/.test(l.sku) ? '' : l.sku, desc: noDash(l.desc), qty: l.qty, price: l.na ? null : l.price })) };
  btn.disabled = true; btn.textContent = 'Sending...';
  telemetry.intent('sales_form_submit', { kind: formKind, company: !!d.company, phone: !!d.phone, notes: !!d.notes, address: d.address || null, dock: d.dock || null, assembly: d.assembly || null, pay: d.pay || null, qty: b.qty, whiteGlove: !!st.whiteGlove });
  flushEvents();
  let sent = false;
  if (!LIVE) sent = 'preview';
  else { try { const r = await send(lead, true); sent = r.ok; } catch (err) { sent = false; } }
  btn.disabled = false;
  if (sent) {
    rqSent = true; btn.textContent = 'Send';
    f.hidden = true; const done = $('#rq-done'); done.hidden = false;
    done.innerHTML = `<h3 class="gdlg__title">Request sent</h3><p>Thanks, ${esc(d.name)}. We'll email your delivered price to <b>${esc(d.email)}</b> soon, from ${SALES_EMAIL}.</p>`
      + (sent === 'preview' ? '<p class="hint">Preview: nothing was sent. On the live site this goes to our team.</p>' : '')
      + '<div class="gdlg__actions"><button class="obtn" type="button" id="rq-ok">Close</button></div>';
    if (sent === true && !lead.test) {

      const v = { currency: 'USD', value: Math.min(Math.round(lastTotal), 50000), test: false };
      try {
        window.dataLayer?.push?.({ event: 'builder_lead', ...v }); window.Shopify?.analytics?.publish?.('builder_lead', v);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'generate_lead', { currency: 'USD', value: v.value });

          if (window.CTB_ADS_SEND_TO) window.gtag('event', 'conversion', { send_to: window.CTB_ADS_SEND_TO, value: v.value, currency: 'USD', transaction_id: requestId });
        }
      } catch (err) {}
    }
    $('#rq-ok').addEventListener('click', closeRequest); $('#rq-ok').focus();
  } else {

    btn.textContent = 'Try again';
    const er = $('#rq-error'); er.hidden = false;
    er.innerHTML = `We couldn't send that just now. Try again, or <a class="textlink" href="${mailtoFor(b, d)}">email us</a>: your build and your answers are already written in.`;
    er.scrollIntoView({ block: 'nearest' });
    logEvent('request_error', { total: Math.round(lastTotal) }); flushEvents(); telemetry.track('sales_form_error', { kind: formKind });
  }
});

// ---------------------------------------------------------------- leave feedback (Jeremy, 2026-10-08)
// A note from the customer about the builder itself, for our own metrics: a 1-5 rating and a few words. The rating and
// the note go out as the telemetry event `feedback_submit`, so they show on the builder analytics page (Feedback card)
// like every other event. A filled honeypot or a submit within 3 s of opening keeps no telemetry.
// The email is optional (John, 2026-10-10: "Email (optional, if you'd like a reply)"). When one is given, the note also
// goes to the os-builder webhook as `feedback_lead` and n8n emails it to sales@ with Reply-To set to the customer
// (Xavier, 2026-10-10). The email goes only there, never into telemetry. A filled honeypot is still sent with
// `website` so n8n marks it [CHECK] rather than losing a real person's note.
const FB_ERROR_DEFAULT = $('#fb-error')?.innerHTML || '';
const FB_MIN_MS = 3000;
let fbOpenedAt = 0, fbSent = false;
function openFeedback() {
  const f = $('#fb-form'); f.reset(); f.querySelectorAll('.fb__scale label').forEach(l => l.classList.remove('is-on'));
  $('#fb-done').hidden = true; f.hidden = false;
  const er = $('#fb-error'); er.hidden = true; er.innerHTML = FB_ERROR_DEFAULT;
  const btn = $('#fb-send'); btn.disabled = false; btn.textContent = 'Send';
  fbOpenedAt = Date.now(); fbSent = false;
  $('#fb-dialog').showModal(); f.elements.rating[0].focus();
  telemetry.track('feedback_open');
}
const closeFeedback = () => $('#fb-dialog').close();
$('#btn-feedback').addEventListener('click', openFeedback);
$('#fb-close').addEventListener('click', closeFeedback);
$('#fb-cancel').addEventListener('click', closeFeedback);
$('#fb-dialog').addEventListener('close', () => { if (fbOpenedAt && !fbSent) telemetry.track('feedback_cancel'); });   // X, Cancel or Esc
$('#fb-form').addEventListener('change', e => { if (e.target.name === 'rating') $('#fb-form').querySelectorAll('.fb__scale label').forEach(l => l.classList.toggle('is-on', l.contains(e.target))); });
$('#fb-form').addEventListener('submit', async e => {
  e.preventDefault();
  const f = e.currentTarget, d = Object.fromEntries(new FormData(f)), btn = $('#fb-send'), er = $('#fb-error');
  const message = String(d.message || '').trim(), email = String(d.email || '').trim();
  // say which part is missing, not the whole list (the email is optional, so it only counts when it is typed and wrong)
  const missing = [!d.rating && 'pick a rating', message.length < 3 && 'write a few words', !f.elements.email.checkValidity() && 'check the email address'].filter(Boolean);
  const ok = f.checkValidity() && !missing.length; er.innerHTML = missing.length ? 'Please ' + missing.join(missing.length > 2 ? ', ' : ' and ').replace(/, ([^,]*)$/, ' and $1') + '.' : FB_ERROR_DEFAULT; er.hidden = ok;
  if (!ok) { (message.length < 3 && d.rating ? f.elements.message : f.querySelector('input:invalid, textarea:invalid'))?.focus(); telemetry.track('feedback_invalid'); return; }
  const trap = !!d.website, tooFast = Date.now() - fbOpenedAt < FB_MIN_MS;
  if (!trap && !tooFast) telemetry.intent('feedback_submit', { rating: +d.rating, message: message.slice(0, 1000), reply: !!email });   // intent: the build summary rides along; never the email
  // reply wanted: hand the note to sales@ through the webhook. null = no email given; 'preview' = not the live site
  let mailed = null;
  if (email && !tooFast) {
    btn.disabled = true; btn.textContent = 'Sending...';
    const lead = { event: 'feedback_lead', email, rating: +d.rating, note: message.slice(0, 1000), build: buildSummary(), test: isTestVisit(), ...(trap ? { website: String(d.website).slice(0, 200) } : {}) };
    if (!LIVE) mailed = 'preview';
    else { try { mailed = (await send(lead, true)).ok; } catch (err) { mailed = false; } }
    btn.disabled = false; btn.textContent = 'Send';
    if (mailed === false) telemetry.track('feedback_lead_error');
  }
  fbSent = true;
  f.hidden = true; const done = $('#fb-done'); done.hidden = false;
  const fbMailto = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent('Builder feedback')}&body=${encodeURIComponent(`Rating: ${d.rating} of 5\n${message.slice(0, 1000)}\n\n${buildSummary().link}`)}`;
  done.innerHTML = '<h3 class="gdlg__title">Thank you</h3>'
    + (mailed === true || mailed === 'preview' ? `<p>Your note went to the team, and we'll reply to <b>${esc(email)}</b>.</p>`
      : mailed === false ? `<p>Your note reached us, but we couldn't pass on your email just now. For a reply, <a class="textlink" href="${fbMailto}">email ${SALES_EMAIL}</a>: your note is already written in.</p>`
      : '<p>Your note went to the team. It helps us make the builder better for the next person.</p>')
    + (mailed === 'preview' ? '<p class="hint">Preview: nothing was sent. On the live site this goes to our team.</p>' : '')
    + '<div class="gdlg__actions"><button class="obtn" type="button" id="fb-ok">Close</button></div>';
  $('#fb-ok').addEventListener('click', closeFeedback); $('#fb-ok').focus();
});

const b64e = s => btoa(unescape(encodeURIComponent(s))), b64d = s => decodeURIComponent(escape(atob(s)));
function serialize() {
  const o = { ...st, finish: st.finish?.name, midFinish: st.midFinish?.name, baseFinish: st.baseFinish?.name, sideFinish: st.sideFinish?.name };
  return b64e(JSON.stringify(o));
}
function restore() {
  const c = new URLSearchParams(location.search).get('cfg'); if (!c) return;
  try {
    const o = JSON.parse(b64d(c));
    Object.assign(st, o, { finish: fin(o.finish), midFinish: fin(o.midFinish), baseFinish: fin(o.baseFinish), sideFinish: o.sideFinish ? fin(o.sideFinish) : null });
    if (o.baseLinked === undefined) st.baseLinked = !o.baseFinish || o.baseFinish === o.finish;
  } catch (e) { console.warn('bad cfg', e); }
}
function cfgURL() { const u = new URL(location.href); for (const k of [...Object.keys(TRACK), 'os_test', 'src']) u.searchParams.delete(k); u.searchParams.set('cfg', serialize()); u.hash = ''; return u; }
async function shareLink(btn) {
  const url = cfgURL().toString();
  telemetry.intent('share_link');
  try { await navigator.clipboard.writeText(url); btn.textContent = 'Link copied ✓'; setTimeout(() => (btn.textContent = 'Copy share link'), 1500); } catch (e) { prompt('Share link', url); }
}
$('#btn-share').addEventListener('click', e => shareLink(e.currentTarget));

const PRINT_DIR = new THREE.Vector3(0.45, 0.32, 1).normalize();
function printShot() {
  const bb = new THREE.Box3().setFromObject(table); if (bb.isEmpty()) return null;
  const ctr = bb.getCenter(new THREE.Vector3());
  const saved = { pos: camera.position.clone(), target: controls.target.clone(), aspect: camera.aspect, w: canvas.width, h: canvas.height };
  controls.target.set(ctr.x, ctr.y * 0.85, ctr.z);
  const dir = PRINT_DIR.clone();

  const dSq = fitDistance(bb, dir, 1.0, 1);
  camera.position.copy(controls.target).add(dir.clone().multiplyScalar(dSq)); camera.lookAt(controls.target); camera.updateMatrixWorld();
  let nx = 0, ny = 0; const p = new THREE.Vector3();
  for (let i = 0; i < 8; i++) { p.set(i & 1 ? bb.max.x : bb.min.x, i & 2 ? bb.max.y : bb.min.y, i & 4 ? bb.max.z : bb.min.z).project(camera); nx = Math.max(nx, Math.abs(p.x)); ny = Math.max(ny, Math.abs(p.y)); }
  const aspect = Math.max(1.5, Math.min(3, nx / Math.max(ny, 1e-3)));
  const W = 2000, H = Math.round(W / aspect), pr = renderer.getPixelRatio();
  renderer.setSize(W / pr, H / pr, false);
  camera.position.copy(controls.target).add(dir.multiplyScalar(fitDistance(bb, dir, 1.08, W / H)));
  camera.lookAt(controls.target);
  if (xray) applyXray(topRef, false);
  renderer.render(scene, camera);
  if (xray) applyXray(topRef, true);
  const url = renderer.domElement.toDataURL('image/png');
  renderer.setSize(saved.w / pr, saved.h / pr, false); camera.aspect = saved.aspect; camera.updateProjectionMatrix();
  camera.position.copy(saved.pos); controls.target.copy(saved.target); controls.update();
  return url;
}
async function preparePrint() {
  $('#print-meta').textContent = `OfficeSimpli  |  Dallas, TX  |  Questions? officesimpli.com/pages/contact  |  Quote date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  const url = printShot(); if (!url) return;
  const img = $('#print-shot'); img.src = url;
  try { await img.decode(); } catch {}
}
window.__preparePrint = preparePrint;
async function printQuote() { telemetry.intent('save_pdf'); await preparePrint(); window.print(); }
$('#btn-print').addEventListener('click', printQuote);
window.addEventListener('keydown', e => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') { e.preventDefault(); printQuote(); } });
window.addEventListener('beforeprint', () => { if (!$('#print-shot').getAttribute('src')) preparePrint(); });

let TABS = ['typicals', 'shape', 'size', 'base', 'finish', 'options', 'credenza', 'quote'];
const TAB_NAMES = { simple: 'Your table', credenza: 'Credenza', typicals: 'Start', shape: 'Top shape', size: 'Size & construction', base: 'Base', finish: 'Finishes', options: 'Options & power', quote: 'Pricing & payment' };
const TAB_SHORT = { simple: 'Your table', credenza: 'Credenza', typicals: 'Start', shape: 'Shape', size: 'Size', base: 'Base', finish: 'Finishes', options: 'Options', quote: 'Pricing' };
let tab = 'typicals', lastTotal = 0, lastSub = 0, lastQty = 1, lastLines = [], lastNotchRisk = 0, typSnap = null, pendingSnap = false;
const visited = new Set();
const narrow = () => matchMedia('(max-width: 900px)').matches;
function showTab(name) {
  if (!TABS.includes(name)) name = TABS[0];
  if (name !== tab) xrayPick = null;   // a new step starts from its own default, never a pick made on another step
  if (name === 'credenza' && tab !== 'credenza') frameTable(false, credView());   // the whole room, from across the table: where the credenza stands is the point

  if (!visited.has(name) && !(name === 'typicals' && !visited.size)) logEvent('step', { step: name });
  tab = name; document.body.dataset.tab = name; visited.add(name);
  const mh = $('#mhead'); if (mh) { mh.hidden = name === 'typicals' || name === 'simple'; $('#mhead-title').textContent = name === 'quote' ? 'Your price' : 'Your table'; }
  telemetry.step(name); if (name === 'finish') telemetry.part(finPart);
  const i = TABS.indexOf(name);
  document.querySelectorAll('.pane').forEach(p => (p.hidden = p.dataset.pane !== name));
  document.querySelectorAll('.step').forEach(s => {
    const j = TABS.indexOf(s.dataset.tab);
    s.classList.toggle('is-active', j === i); s.classList.toggle('is-done', j > 0 && j !== i && visited.has(s.dataset.tab));
    s.classList.toggle('is-past', j < i);
    if (j === i) s.setAttribute('aria-current', 'step'); else s.removeAttribute('aria-current');
  });
  paintProgress();

  setXray(xrayPick ?? (name === 'options' && underTable()));
  const steps = $('#steps');
  const active = $('#steps .step.is-active');
  if (active) steps.scrollTo({ left: active.offsetLeft - (steps.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
  $('#panel-body').scrollTop = 0;
  if (narrow()) {
    document.documentElement.style.setProperty('--steps-mh', steps.offsetHeight + 'px');
    const y = $('#panel').getBoundingClientRect().top + scrollY - steps.offsetHeight - $('#stage').offsetHeight;
    if (scrollY > y) scrollTo({ top: Math.max(0, y) });
  }
  navLabels();
  history.replaceState(null, '', location.pathname + location.search + '#' + name);
}

const finParts = () => [...document.querySelectorAll('#fin-tabs .subtab')].map(b => ({ key: b.dataset.part, name: b.querySelector('.subtab__name').textContent }));
function navLabels() {
  const names = matchMedia('(max-width: 1280px)').matches ? TAB_SHORT : TAB_NAMES, i = TABS.indexOf(tab);
  const parts = tab === 'finish' ? finParts() : [], k = parts.findIndex(p => p.key === finPart);
  const back = k > 0 ? parts[k - 1].name : i > 0 ? names[TABS[i - 1]] : '';
  const next = k >= 0 && k < parts.length - 1 ? parts[k + 1].name : names[TABS[i + 1]];
  const last = i === TABS.length - 1, nx = $('#nav-next');
  $('#nav-back').disabled = i === 0;
  $('#nav-back').textContent = back && !narrow() && !last ? `Back: ${back}` : 'Back';

  const payNow = last && invoiceOnly();
  nx.textContent = last ? (payNow ? 'Pay by invoice' : 'Get my delivered price') : TABS[i + 1] === 'quote' && narrow() ? 'See my price' : next ? `Next: ${narrow() ? TAB_SHORT[TABS[i + 1]] ?? next : next}` : 'Next';
  $('#panel-nav').classList.toggle('is-pay', last); $('#panel-nav').classList.toggle('pay-two', payNow);

  const preset = tab === 'typicals' && !!st.typical, buy = $('#nav-buy');
  buy.hidden = !preset && !payNow; $('#nav-back').hidden = preset;
  if (preset) {
    nx.textContent = 'Customize it'; nx.classList.add('obtn--ghost');

    buy.textContent = untouched() ? `${CHECKOUT ? 'Buy' : 'Choose'} as is · ${money(lastTotal)}` : (CHECKOUT ? 'Review price & buy' : 'Review price');
  } else { nx.classList.remove('obtn--ghost'); if (tab === 'typicals' && TABS[i + 1] === 'shape') nx.textContent = 'Start from scratch'; }

  if (payNow) buy.innerHTML = `Buy it now<span class="obtn__sep"> · </span><span class="obtn__price">${money(lastTotal)}</span>`;
}
function stepFinish(dir) {
  if (tab !== 'finish') return false;
  const parts = finParts(), k = parts.findIndex(p => p.key === finPart), to = parts[k + dir];
  if (!to) return false;
  showFinPart(to.key); $('#panel-body').scrollTop = 0; return true;
}

function paintProgress() {
  const cur = $('#steps .step.is-active'), fill = $('#steps-fill');
  if (!cur || !fill) return;
  const w = TABS.indexOf(tab) > 0 ? Math.max(0, cur.offsetLeft - 24) : 0;
  fill.style.width = w + 'px'; fill.classList.toggle('on', w > 0);
}
addEventListener('resize', paintProgress);
document.fonts?.ready.then(paintProgress);
document.querySelectorAll('.step').forEach(s => s.addEventListener('click', () => showTab(s.dataset.tab)));
$('#mhead-back')?.addEventListener('click', () => $('#nav-back').click());
// phones: the enlarge button grows the 3-D view to the whole screen and back (the stage's ResizeObserver re-fits the camera)
$('#stage-enlarge')?.addEventListener('click', e => { const on = document.body.classList.toggle('stage-full'); e.currentTarget.setAttribute('aria-pressed', String(on)); telemetry.track('stage_enlarge', { on }); });
// pricing step on phones: share / PDF / feedback as links under the terms (the header's buttons are hidden there)
document.querySelectorAll('[data-click]').forEach(b => b.addEventListener('click', () => $(b.dataset.click)?.click()));
// phones: the progress bar sits between the 3-D view and the step's question (the mockup), desktop keeps it under the header;
// the itemized pricing folds under a short summary
{
  const mq = matchMedia('(max-width: 900px)'), steps = $('#steps'), home = steps.nextElementSibling;
  const place = () => {
    if (mq.matches) { $('#panel').before(steps); steps.classList.add('is-under-stage'); } else { home.before(steps); steps.classList.remove('is-under-stage'); }
    const qi = $('#quote-items'); if (qi) qi.open = !mq.matches;
  };
  place(); mq.addEventListener('change', place);
}
$('#nav-back').addEventListener('click', () => {
  if (stepFinish(-1)) return;
  const to = TABS[Math.max(0, TABS.indexOf(tab) - 1)];
  if (to === 'finish') { const parts = finParts(); if (parts.length) finPart = parts[parts.length - 1].key; }
  showTab(to); if (to === 'finish') showFinPart(finPart);
});

$('#nav-buy').addEventListener('click', () => { if (tab === 'quote') return checkout(); telemetry.intent('buy_as_is'); showTab('quote'); });
$('#nav-next').addEventListener('click', () => {
  if (stepFinish(1)) return;
  const i = TABS.indexOf(tab); if (i === TABS.length - 1) return openRequest(false);
  if (TABS[i + 1] === 'finish') finPart = 'top';
  showTab(TABS[i + 1]); if (TABS[i + 1] === 'finish') showFinPart(finPart);
});

if (!QS.get('view')) { let t; new ResizeObserver(() => { clearTimeout(t); t = setTimeout(() => frameTable(true), 220); }).observe($('#stage')); }

function stepValues(top) {
  const fam = CAT.families[st.famCode];
  const typ = TYP?.typicals.find(x => x.code === st.typical);
  const round = top.shape === 'round' || top.shape === 'square';
  const metal = fam.metal ? ((fam.colors || {})[st.metalCode] || METALS[st.metalCode]?.name || '').replace('*', '') : '';

  const gq = st.grommet.sku ? st.grommet.qty : 0, aq = Object.values(st.acc).reduce((a, b) => a + (b || 0), 0);
  const opts = [st.rounded && 'Rounded corners', st.reveal && 'Reveal', st.baseOpts['OP-CONF-71'] && 'Kickplate',
    st.doors && `${st.doors} access door${st.doors > 1 ? 's' : ''}`, gq && `${gq} grommet${gq > 1 ? 's' : ''}`, aq && `${aq} accessor${aq > 1 ? 'ies' : 'y'}`].filter(Boolean).join(', ');
  const baseLam = !fam.metal || fam.wirebox;
  const cz = st.credenza, czFam = (CAT.credenzas || []).find(c => c.code === cz?.type);
  const v = {
    typicals: typ ? (typSnap && core() !== typSnap ? 'Custom, started from a preset' : typ.cfg.name) : 'Custom build',
    shape: CAT.shapes[st.shape],
    size: (round ? `${top.depth}"` : `${top.depth}" × ${top.length}"`) + ` (${ftDims(top)})`,
    base: fam.name,
    finish: (baseLam && st.baseFinish?.name !== st.finish?.name ? `Top ${st.finish?.name} · Base ${st.baseFinish?.name}` : [st.finish?.name, fam.metal && !fam.wirebox ? metal : ''].filter(Boolean).join(' · '))
      + (st.baseOpts['OP-CONF-SVP'] ? ' · Silver sides' : st.sideFinish && st.sideFinish.name !== st.baseFinish?.name ? ` · ${st.sideFinish.name} sides` : ''),
    options: opts || 'None added',
    credenza: czFam ? `${cz.qty > 1 ? '2 × ' : ''}${cz.width}" ${czFam.height > 30 ? 'buffet' : 'standard'}${czFam.bays === 3 ? ' media' : czFam.drawers ? ' w/ drawers' : ''}${cz.place && cz.place !== 'back' ? ' · ' + (st.shape === 'sightline' ? { front: 'in front', left: 'wide end', right: 'screen end' } : { right: 'front/back' })[cz.place] : ''}${cz.finish ? ' · ' + cz.finish : ''}` : 'None',   // short: it sits under the step name
    quote: DEV ? money(lastTotal) : '',
  };
  document.querySelectorAll('[data-val]').forEach(e => (e.textContent = v[e.dataset.val] ?? ''));
}

const core = () => JSON.stringify({ ...st, typical: null, whiteGlove: null, qty: null, finish: st.finish?.name, midFinish: st.midFinish?.name, baseFinish: st.baseFinish?.name, sideFinish: st.sideFinish?.name });

function settle() { const top = validate(); buildSizeUI(top); buildBaseUI(top); buildFinishUI(top); buildOptionsUI(top); return top; }
function update() {
  const top = settle();
  buildTypicalsUI(); buildShapeChips(); buildQuote(top); if (LOCK) buildSimpleUI(top); else buildCredenzaStep(top);
  if (PDP && window.parent !== window) window.parent.postMessage({ type: 'os-byoct', typical: PDP, total: lastTotal, untouched: untouched(),
    title: $('#quote-title').textContent, link: cfgURL().toString() }, '*');
  document.body.classList.toggle('has-under', underTable());
  navLabels();
  if (pendingSnap) { typSnap = core(); pendingSnap = false; }
  stepValues(top);

  const u = cfgURL(); history.replaceState(null, '', u.pathname + u.search + location.hash);

  telemetry.config(flatCfg(), (STEP_FIELDS[tab] || []).filter(f => !(f === 'baseFinish' && st.baseLinked)), cfgSilent, { total: lastTotal }); cfgSilent = false;
  return rebuild().catch(e => console.error('build failed', e));
}

const STEP_FIELDS = {
  simple: ['sizeKey', 'height', 'baseSku', 'finish', 'midFinish', 'baseFinish', 'baseLinked', 'metalCode', 'grommet.', 'credenza.'],
  shape: ['shape'], size: ['sizeKey', 'pieces', 'multi', 'laminate', 'edge'], base: ['famCode', 'baseSku', 'height'],
  finish: ['finish', 'midFinish', 'baseFinish', 'sideFinish', 'sideMatch', 'baseLinked', 'metalCode', 'kick', 'baseOpts.OP-CONF-SVP'],
  options: ['rounded', 'reveal', 'revealColor', 'doors', 'grommet.', 'acc.', 'baseOpts.'], credenza: ['credenza.'], quote: ['whiteGlove', 'qty'],
};
let cfgSilent = false;

function flatCfg() {
  const f = {};
  for (const [k, v] of Object.entries(st)) {
    if (k === 'typical') continue;
    if (v && typeof v === 'object') { if ('name' in v) f[k] = v.name; else for (const [kk, vv] of Object.entries(v)) if (vv || (k === 'grommet' && vv === 0)) f[`${k}.${kk}`] = vv; }
    else if (v !== null && v !== undefined) f[k] = v;
  }
  return f;
}

function buildSummaryTelemetry() {
  const fam = CAT.families[st.famCode];
  return {
    typical: st.typical || undefined, untouched: st.typical ? untouched() : undefined, shape: st.shape, size: st.sizeKey, base: st.famCode,
    top: st.finish?.name, baseFinish: fam?.metal && !fam?.wirebox ? undefined : st.baseFinish?.name, metal: fam?.metal ? st.metalCode : undefined,
    grommet: st.grommet.sku || undefined, grommets: st.grommet.sku ? st.grommet.qty : 0,
    accessories: Object.values(st.acc).reduce((a, b) => a + (b || 0), 0), whiteGlove: st.whiteGlove, qty: st.qty || 1, total: lastTotal,
  };
}

function tidyText(cat) {
  const tidy = s => String(s).replace(/^\s*—\s*$/, '').replace(/\s*—\s*/g, ': ').replace(/\bcentre\b/g, 'center').replace(/\bcolour/g, 'color');
  for (const f of Object.values(cat.families)) f.name = tidy(f.name);
  for (const o of cat.options) if (o.label) o.label = tidy(o.label);
  for (const a of cat.accessories) a.label = tidy(a.label);
}

(async function init() {
  CAT = await (await fetch(dataUrl('data/catalog.json'))).json();
  applyRetail(CAT);
  addCustomLengths(CAT);
  priceGrommets(CAT);
  tidyText(CAT);
  CAT.metals = Object.fromEntries(Object.entries(METALS).map(([k, v]) => [k, v.name]));
  await loadManifest();
  await loadLegs();
  try { TYP = await (await fetch(dataUrl('data/typicals.json'))).json(); } catch (e) { console.warn('typicals.json missing', e); }
  document.body.classList.toggle('dev', DEV);
  if (QS.has('thumb')) document.body.classList.add('thumb');
  restore();
  const pdpTyp = PDP && TYP?.typicals.find(x => x.code === PDP);
  if (pdpTyp) {
    LOCK = { shape: pdpTyp.cfg.shape, famCode: pdpTyp.cfg.famCode };
    document.body.classList.add('pdp');
    TABS = ['simple', 'quote'];
    renumberSteps();
    if (!QS.has('cfg') || st.typical !== pdpTyp.code) { applyPreset(pdpTyp); pendingSnap = true; cfgSilent = true; }
    Object.assign(st, LOCK);
    anchor = presetAnchor(pdpTyp);
  } else { installCredenzaStep(); if (!QS.has('cfg')) scratchLook(); }
  if (!pdpTyp && st.typical) { const t = TYP?.typicals.find(x => x.code === st.typical); if (t) anchor = presetAnchor(t); }   // a shared preset keeps its listed price
  telemetry.start({ entry: pdpTyp ? 'product_page' : QS.has('cfg') ? (st.typical ? 'shared_preset' : 'shared_link') : 'fresh', summarize: buildSummaryTelemetry });
  showTab(location.hash.slice(1) || (pdpTyp ? TABS[0] : new URLSearchParams(location.search).has('cfg') ? 'quote' : 'typicals'));
  await update();

  window.CTB_READY = true;
  { const ld = $('#stage-loading'); if (ld) ld.hidden = true; document.querySelectorAll('.typ-skel').forEach(n => n.remove()); }   // placeholders go even if typicals.json failed
  telemetry.track('ready', { ms: Math.round(performance.now()) });   // load time: page open to a drawn table (John 2026-10-10: tracking load times is very important)
  { const late = $('#noload'); if (late && !late.hidden) { late.hidden = true; document.body.classList.remove('no-gl'); } }
  logEvent('open', { preset: st.typical || null, shared: QS.has('cfg') });
  if (!new URLSearchParams(location.search).get('view')) frameTable(false, tab === 'credenza' ? credView() : null);   // opened on the Credenza step: from across the table
})().catch(e => {
  console.error('Build your own: could not start', e);
  { const ld = $('#stage-loading'); if (ld) ld.hidden = true; }
  { const sp = $('#noload-spin'), h = $('#noload h2'), tx = $('#noload p'); if (sp) sp.hidden = true;   // a real failure, not a slow load: say so
    if (h) h.textContent = 'The 3D builder could not load';
    if (tx) tx.textContent = "Please reload the page, or try another browser. Or tell us what you're after and we'll email you a delivered price."; }
  const p = $('#noload'); if (p) { p.hidden = false; document.body.classList.add('no-gl'); }
  if (window.CTB_REPORT) CTB_REPORT('data');
});
